import './api';
import React, { useEffect, useMemo, useLayoutEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import ManageOEMModal from './components/ManageOEMModal';
import ManageSKUModal from './components/ManageSKUModal';
import AddOneOffModal from './components/AddOneOffModal';
import KittingModal from './components/KittingModule/KittingModal';
import DataCollectionOverviewByModel from './DataCollectionOverviewByModel';
import { FaCheckCircle, FaCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';


ModuleRegistry.registerModules([ AllCommunityModule ]);

const KIT_ID_PREFIX = 'OEMKIT';

// Server cancels the overview query at 60s and returns a friendly 504; the client
// gives up slightly later as a backstop (e.g. if the server itself is unresponsive).
const OVERVIEW_LOAD_TIMEOUT_MS = 65000;

const DEFAULT_COLUMN_ORDER = [
  'Make',
  'ModelName',
  'ModelID',
  'Year',
  'YearID',
  'Region',
  'ItemType',
  'FitmentLocation',
  'SKUGroup',
  'OEMNumber',
  'Quantity',
  'OEMNote',
  'AdditionalFitmentInfo',
  'FicheURL'
];

const ensureColumnCoverage = (order = []) => {
  const sanitized = (order || []).filter(col => DEFAULT_COLUMN_ORDER.includes(col));
  const missing = DEFAULT_COLUMN_ORDER.filter(col => !sanitized.includes(col));
  return [...sanitized, ...missing];
};

const formatModelLabel = (meta, fallback) => {
  if (!meta) return fallback || 'Model';
  const make = meta.make ?? meta.Make;
  const modelName = meta.modelName ?? meta.ModelName;
  const streetName = meta.streetName ?? meta.StreetName;
  const year = meta.year ?? meta.Year;
  const region = meta.region ?? meta.Region;
  const parts = [make, modelName];
  if (streetName) parts.push(streetName);
  if (year !== null && year !== undefined) parts.push(year);
  const base = parts.filter(Boolean).join(' ');
  return region ? `${base} (${region})` : base;
};

// Global axios interceptor for debugging requests/responses (fast, conditional log)
axios.interceptors.request.use(config => {
  //console.log('Axios Request Sent:', {
    //method: String(config.method || '').toUpperCase(),
   // url: (axios.defaults.baseURL || '') + (config.url || ''),
   // params: config.params,
   // data: 'request sent'
  //});
  return config;
}, error => {
  console.error('Axios Request Error:', error);
  return Promise.reject(error);
});

axios.interceptors.response.use(response => {
  //console.log('Axios Response Received:', {
    //url: response?.config?.url,
    //status: response?.status,
   // data: 'response received'
  //});
  return response;
}, error => {
  const message = typeof error?.message === 'string' ? error.message : '';
  const code = error?.code || '';
  const name = error?.name || '';
  const isCanceled =
    code === 'ERR_CANCELED' ||
    name === 'CanceledError' ||
    message.toLowerCase().includes('canceled') ||
    message.toLowerCase().includes('cancelled');
  if (!isCanceled) {
    console.error('Axios Response Error:', error.response ? {
      status: error.response.status,
      data: 'Axios error'
    } : error.message);
  }
  return Promise.reject(error);
});


function useVisibilityChange(onChange) {
  useEffect(() => {
    const handleVisibility = () => {
      onChange(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [onChange]);
}

function useValueDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

function debounce(fn, delay) {
  let timer;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
  debounced.flush = () => {
    if (timer) {
      clearTimeout(timer);
      fn();
    }
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export default function DataCollectionOverview({ permissions, isDark }) { // Receive permissions prop from App (for write checks) and theme prop (existing styling)
  const [ficheModalVisible, setFicheModalVisible] = useState(false);
  const [currentFiche, setCurrentFiche] = useState(null);
  const [editedExtracted, setEditedExtracted] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const [formatToggle, setFormatToggle] = useState(false); // Toggle for OEM formatting
  const [pickerVisible, setPickerVisible] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState({});
  const [oemPopupVisible, setOemPopupVisible] = useState(false);
  const [oemChoices, setOemChoices] = useState([]);
  const [oemPopupRowIdx, setOemPopupRowIdx] = useState(null);
  const [extractedPerFitment, setExtractedPerFitment] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [oemPopupLine, setOemPopupLine] = useState(null);
  const [siteTemplates, setSiteTemplates] = useState([]); // Fetch from API if needed
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSubGroup, setSelectedSubGroup] = useState('');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [filters, setFilters] = useState({
    Make: '',
    ModelName: '',
    ModelID: '',
    Year: '',
    YearID: '',
    Region: '',
    ItemType: '',
    FitmentLocation: '',
    SKUGroup: '',
    OEMNumber: '',
    Quantity: '',
    OEMNote: '',
    AdditionalFitmentInfo: ''
  });
  const debouncedFilters = useValueDebounce(filters, 800); // Renamed and increased to 800ms to reduce refreshes
  const [editing, setEditing] = useState({ rowIdx: null, field: '', value: '' });
  const [focusPos, setFocusPos] = useState({ row: null, col: null });
  const inputRefs = useRef({}); // { "row-col": HTMLInputElement }
  const createInFlightRef = useRef(new Set()); // guard to prevent duplicate POSTs
  const abortControllerRef = useRef(null); // cancel in-flight overview requests
  const [hasMore, setHasMore] = useState(true);
  const lastRowRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const scrollRef = useRef(null); // Ref for the scrollable div
  const scrollPositionRef = useRef(0); // To store scroll position before reload
  const [gridHeight, setGridHeight] = useState(null); // Dynamic height for grid area
  const bottomBarRef = useRef(null); // Ref for fixed bottom bar
  const CACHE_KEY = 'dataCollectionCache';
  const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
  const CACHE_SIZE_LIMIT_BYTES = 4.5 * 1024 * 1024; // ~4.5 MB safety cap
  const [cacheLoaded, setCacheLoaded] = useState(false);
  const [isAppending, setIsAppending] = useState(false);
  const [columnOrder, setColumnOrder] = useState(() => [...DEFAULT_COLUMN_ORDER]);
  const [columnWidths, setColumnWidths] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyFitmentKey, setHistoryFitmentKey] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [prevOrder, setPrevOrder] = useState(() => [...DEFAULT_COLUMN_ORDER]);
  const [prevWidths, setPrevWidths] = useState(columnWidths);
  const username = localStorage.getItem('username') || 'guest';
  const COL_PREF_KEY = `columnPrefs_${username}`;
  const [uniqueModels, setUniqueModels] = useState([]);
  const [selectedModelKey, setSelectedModelKey] = useState('');
  const [fillValue, setFillValue] = useState('');
  const [customFillValue, setCustomFillValue] = useState('');
  const [lastBulkChanges, setLastBulkChanges] = useState([]);
  const [isUndone, setIsUndone] = useState(false);
  const [copySourceModelKey, setCopySourceModelKey] = useState('');
  const [copyFields, setCopyFields] = useState({ Quantity: true, AdditionalFitmentInfo: true });
  const [copyOnlyEmpty, setCopyOnlyEmpty] = useState(true);
  const [copyProgress, setCopyProgress] = useState(0); // For progress bar (0-100)
  const [isLayoutCollapsed, setIsLayoutCollapsed] = useState(true);
  const [isGroupCollapsed, setIsGroupCollapsed] = useState(false);
  const [isCopyCollapsed, setIsCopyCollapsed] = useState(true);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [candidateModels, setCandidateModels] = useState([]);
  const onModelSelectRef = useRef(null);
  const [modelOverviewModal, setModelOverviewModal] = useState(null);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelModalKey, setModelModalKey] = useState(null);
  const editableFields = ['SKUGroup', 'OEMNumber', 'Quantity', 'OEMNote', 'AdditionalFitmentInfo', 'FicheURL'];
  const canDataCollectionWrite = permissions?.['Data Collection/Reviewing'] === 'write';
  const canManageKits = permissions?.Kitting === 'write' || canDataCollectionWrite;

const openModelOverview = (row) => {
  if (!canDataCollectionWrite) {
    window.alert('You do not have permission to open the model overview.');
    return;
  }
  if (!row?.ModelKey) return;
  const parts = [row.Make, row.ModelName];
  if (row.Year != null && row.Year !== undefined) {
    parts.push(row.Year);
  }
  const baseLabel = parts.filter(Boolean).join(' ');
  const label = row.Region ? `${baseLabel} (${row.Region})` : baseLabel;
  // No group keys — load the model overview unfiltered
  setModelOverviewModal({
    modelKey: row.ModelKey,
    label
  });
};

  const closeModelOverview = () => setModelOverviewModal(null);

  // One-Off tracking and context for modal
  const [allOneOffsForSPG, setAllOneOffsForSPG] = useState([]); // for quick "is one-off row?" checks
  const [oneOffContext, setOneOffContext] = useState(null);
  // Manage modal
// ─── Manage OEM modal state & opener ──────────────────────────
  const [manageOpen, setManageOpen] = useState(false);
  const [manageInitialOEM, setManageInitialOEM] = useState('');
  const [manageInitialOEMKey, setManageInitialOEMKey] = useState(null);
  const [manageSkuOpen, setManageSkuOpen] = useState(false);
  const [manageInitialSKUGroup, setManageInitialSKUGroup] = useState('');
  const [kitModalState, setKitModalState] = useState({ open: false, kitId: null, fitment: null });
// Incomplete OEM toggle button related stuff
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(true);
  const [lastFocusedKey, setLastFocusedKey] = useState(null);
  const [lastFocusedField, setLastFocusedField] = useState(null);
  const [lastFocusedCol, setLastFocusedCol] = useState(null);



/**
 * Opens the Manage OEM modal using the OEMNumber from the currently
 * focused row (if any). Falls back to empty if nothing is selected.
 */
  const openManageForCurrent = () => {
  const r = (focusPos && focusPos.row != null) ? rows[focusPos.row] : null;
  const initialNum = (r?.OEMNumber || '').trim();
  const initialKey = r?.OEMKey ?? null;
  setManageInitialOEM(initialNum);
  setManageInitialOEMKey(initialKey);
  setManageOpen(true);
};





// History highlight visibility (driven by App header button)
  const [showHistoryHighlights, setShowHistoryHighlights] = useState(() => {
  try {
    return JSON.parse(localStorage.getItem('showHistoryHighlights') ?? 'true');
  } catch {
    return true;
  }
});

// Listen for header toggle
useEffect(() => {
  const onToggle = (e) => {
    setShowHistoryHighlights(!!e.detail?.enabled);
  };
  window.addEventListener('historyHighlightToggle', onToggle);
  return () => window.removeEventListener('historyHighlightToggle', onToggle);
}, []);



  // Load all one-offs for selected sub-group so we can tag rows that are one-off lines
useEffect(() => {
  let cancelled = false;
  (async () => {
    if (!selectedSubGroup) {
      setAllOneOffsForSPG([]);
      return;
    }
    try {
      const data = await fetchOneOffs(selectedSubGroup, selectedGroup, null);
      if (!cancelled) setAllOneOffsForSPG(data || []);
    } catch (err) {
      console.error('Failed to load one-offs for SPG:', err);
      if (!cancelled) setAllOneOffsForSPG([]);
    }
  })();
  return () => { cancelled = true; };
}, [selectedSubGroup, selectedGroup]);


  // One-off detector: true for ghost one-offs OR persisted rows that are promoted in this SPG
const isOneOffRow = React.useCallback(
  (r) => {
    if (!r) return false;
    if (r.IsGhostOneOff) return true;
    // match by BOTH FitmentLocationKey and ItemTypeKey to avoid false positives
    return allOneOffsForSPG.some(
      (o) => o.FitmentLocationKey === r.FitmentLocationKey && o.ItemTypeKey === r.ItemTypeKey
    );
  },
  [allOneOffsForSPG]
);


// Function to apply filters and reload overview with current filter state
const applyFilters = () => {
  immediateLoadOverview(false); // Use immediate load to apply current filters without debounce delay
};

  const savePrefs = () => {
    try {
      if (deepEqual(columnOrder, prevOrder) && deepEqual(columnWidths, prevWidths)) {
        console.log('No changes detected, skipping save.');
        setSaveStatus('idle');
        return;
      }
      const prefs = JSON.stringify({ order: columnOrder, widths: columnWidths });
      localStorage.setItem(COL_PREF_KEY, prefs);
      console.log('Saved column prefs:', prefs);
      setPrevOrder([...columnOrder]); // Deep copy array
      setPrevWidths({ ...columnWidths }); // Deep copy object
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000); // Fade after 3s
    } catch (err) {
      console.error('Failed to save column prefs:', err);
      setSaveStatus('error');
      // Alert only on manual button press (handled in onClick)
    }
  };

  const debouncedSavePrefs = debounce(() => {
    setSaveStatus('saving');
    savePrefs();
  }, 500);

  // --- Fast OEM type resolver with cache (non-blocking feel) ---
const oemTypeCacheRef = useRef(new Map());
const resolveOEMTypeFast = async (oemNumber) => {
  const key = (oemNumber || '').trim();
  if (!key) return null;
  if (oemTypeCacheRef.current.has(key)) {
    return oemTypeCacheRef.current.get(key);
  }
  try {
    const { data } = await axios.get('/api/oem/type', { params: { number: key } });
    const t = data?.oemType ?? null;
    oemTypeCacheRef.current.set(key, t);
    return t;
  } catch {
    return null;
  }
};

// Robust loader for One-Off groups (handles old/new server signatures gracefully)
async function fetchOneOffs(spgKey, pgKey, itemTypeKey = null) {
  // 1) Preferred: new API expects both SubProductGroupKey and parent ProductGroupKey
  try {
    const { data } = await axios.get(
      `/api/sub-product-groups/${spgKey}/one-off-groups`,
      { params: { productGroupKey: pgKey, itemTypeKey: itemTypeKey ?? undefined } }
    );
    if (Array.isArray(data)) return data;
  } catch { /* try fallbacks below */ }

  // 2) Fallback: API that ignored productGroupKey
  try {
    const { data } = await axios.get(
      `/api/sub-product-groups/${spgKey}/one-off-groups`,
      { params: { itemTypeKey: itemTypeKey ?? undefined } }
    );
    if (Array.isArray(data)) return data;
  } catch { /* try final fallback */ }

  // 3) Final fallback: some older routes used parentKey/pgKey as the param name
  try {
    const { data } = await axios.get(
      `/api/sub-product-groups/${spgKey}/one-off-groups`,
      { params: { parentKey: pgKey, pgKey, itemTypeKey: itemTypeKey ?? undefined } }
    );
    if (Array.isArray(data)) return data;
  } catch { /* no-op */ }

  return [];
}



  // ─── Load all ProductGroups ─────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/api/product-groups');
        setGroups(r.data);
      } catch (err) {
        console.error('Failed to load product groups:', err);
        if (err.response?.status === 401) {
          console.warn('Unauthorized while loading product groups; suppressing hard redirect.');
        }
      }
    })();
  }, []);

  // ─── When group changes, load Sub-Groups ───
  useEffect(() => {
    if (!selectedGroup) {
      setSubGroups([]);
      setSelectedSubGroup('');
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`/api/product-groups/${selectedGroup}/sub-groups`);
        setSubGroups(r.data);
      } catch (err) {
        console.error('Failed to load sub-groups:', err);
        if (err.response?.status === 401) {
          console.warn('Unauthorized while loading sub-groups; suppressing hard redirect.');
        }
      }
    })();
  }, [selectedGroup]);
 
useEffect(() => {
  // Fetch templates for dropdown (optional)
  axios.get('/api/site-templates').then(res => setSiteTemplates(res.data));  // Assume endpoint added if needed
}, []);

/**
 * Ensure API requests go to the Express server in dev.
 * If the UI is on :3000, direct Axios to :5000.
 * (In prod/same-origin this resolves to "" and stays relative.)
 */

// ─── Fetch the overview matrix ──────────────
const buildRowKey = row => {
  const modelKey = row?.ModelKey ?? 'unknown-model';
  const locationKey = row?.FitmentLocationKey ?? 'unknown-location';
  if (row?.FitmentKey) {
    return `${modelKey}-${locationKey}-${row.FitmentKey}`;
  }
  return `ghost-${modelKey}-${locationKey}`;
};

const mergeRows = (previousRows, incomingRows, append) => {
  const combined = append ? [...previousRows, ...(incomingRows || [])] : incomingRows || [];
  const unique = new Map();
  combined.forEach(row => {
    const key = buildRowKey(row);
    if (!unique.has(key)) {
      unique.set(key, row);
    }
  });
  return Array.from(unique.values());
};

const loadOverview = (append = false) => {
  if (!selectedGroup) return;
  // Cancel any in-flight request before starting a new one
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  const controller = new AbortController();
  abortControllerRef.current = controller;
  setLoading(true);
  setCacheLoaded(false); // Ensure cache is updated after fresh load
  if (append) {
    setIsAppending(true);
  }
  if (!append) {
    setRows([]);
  }
  const modelsPerPage = 15;
  let qp = `?pgKey=${selectedGroup}` +
    (selectedSubGroup ? `&spgKey=${selectedSubGroup}` : '') +
    `&limit=${modelsPerPage}&offset=${(page - 1) * modelsPerPage}` +
    (showIncompleteOnly ? '&incompleteOEM=true' : '');
  Object.entries(debouncedFilters).forEach(([key, val]) => {
    if (val) {
      qp += `&${key}=${encodeURIComponent(val)}`;
    }
  });
  // Log query for debugging no results
  console.log('Loading with query:', qp);
  axios.get(`/api/data-collection-overview${qp}`, { signal: controller.signal, timeout: OVERVIEW_LOAD_TIMEOUT_MS })
    .then(r => {
      setRows(prev => mergeRows(prev, r.data.rows, append));
      console.log(`Fetched ${r.data.rows.length} rows, total ${r.data.totalCount} models`);
      setTotalCount(r.data.totalCount || 0);
      setHasMore(page * modelsPerPage < r.data.totalCount);
    })
    .catch(err => {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return; // Silently ignore cancelled requests
      console.error(err);
      if (err?.code === 'ECONNABORTED' || err?.response?.status === 504) {
        alert(err?.response?.data?.error || 'Loading took longer than 60 seconds and was stopped. Try narrowing the filters.');
      }
    })
    .finally(() => {
      if (!controller.signal.aborted) {
        setLoading(false);
        setIsAppending(false);
      }
    });
};

// Function to load overview immediately with current filters (bypasses debounce for Enter key)
const immediateLoadOverview = (append = false) => {
  if (!selectedGroup) return;
  // Cancel any in-flight request before starting a new one
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  const controller = new AbortController();
  abortControllerRef.current = controller;
  setLoading(true);  // Show loading indicator
  setCacheLoaded(false);  // Reset cache flag
  if (append) {
    setIsAppending(true);  // Flag for append mode
  }
  if (!append) {
    setRows([]);  // Clear rows for fresh load
  }
  const modelsPerPage = 15;  // Page size constant
  let qp =  // Build query params string
    `?pgKey=${selectedGroup}` +
    (selectedSubGroup ? `&spgKey=${selectedSubGroup}` : '') +
    `&limit=${modelsPerPage}&offset=${(page - 1) * modelsPerPage}` +
    (showIncompleteOnly ? '&incompleteOEM=true' : '');
  Object.entries(filters).forEach(([key, val]) => {  // Use current filters (not debounced)
    if (val) {
      qp += `&${key}=${encodeURIComponent(val)}`;
    }
  });
  console.log('Immediate Load with query:', qp);  // Log for debug
  axios.get(`/api/data-collection-overview${qp}`, { signal: controller.signal, timeout: OVERVIEW_LOAD_TIMEOUT_MS })
    .then(r => {
      setRows(prev => mergeRows(prev, r.data.rows, append));
      console.log(`Fetched ${r.data.rows.length} rows, total ${r.data.totalCount} models`);
      setTotalCount(r.data.totalCount || 0);
      setHasMore(page * modelsPerPage < r.data.totalCount);  // Update hasMore flag
    })
    .catch(err => {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return; // Silently ignore cancelled requests
      console.error(err);
      if (err?.code === 'ECONNABORTED' || err?.response?.status === 504) {
        alert(err?.response?.data?.error || 'Loading took longer than 60 seconds and was stopped. Try narrowing the filters.');
      }
    })
    .finally(() => {
      if (!controller.signal.aborted) {
        setLoading(false);
        setIsAppending(false);
      }
    });
};

useEffect(() => {
  if (!hasMore || !lastRowRef.current || loading) return;
  const observer = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting && !loading) {
      // Capture scroll before loading more
      if (scrollRef.current) {
        scrollPositionRef.current = scrollRef.current.scrollTop;
      }
      setPage(p => p + 1);
      loadOverview(true);
    }
  }, { threshold: 0.1, rootMargin: '0px 0px 300px 0px' });  // Trigger 300px before bottom
  observer.observe(lastRowRef.current);
  return () => observer.disconnect();
}, [hasMore, rows, loading]);
  // Restore focus/scroll after rows load (if last focused still in set)
  useEffect(() => {
    if (!rows.length || !lastFocusedKey) return;
    const newIdx = rows.findIndex(r => r.FitmentKey === lastFocusedKey);
    if (newIdx === -1) return; // Not loaded yet (user can scroll to load more)
    const newCol = lastFocusedCol || 0;
    setFocusPos({ row: newIdx, col: newCol });
    setEditing({ rowIdx: newIdx, field: lastFocusedField, value: rows[newIdx][lastFocusedField] || '' });
    // Center scroll on the input
    setTimeout(() => {
      const input = inputRefs.current[`${newIdx}-${newCol}`];
      if (input && scrollRef.current) {
        const container = scrollRef.current;
        const inputTop = input.offsetTop;
        const inputHeight = input.offsetHeight;
        const containerHeight = container.offsetHeight;
        container.scrollTop = inputTop - (containerHeight / 2) + (inputHeight / 2);
      }
    }, 0);
  }, [rows]);

  // Handle OEM blur for line number lookup
const handleOEMBlur = async (i) => {
  if (typeof handleChange === 'function' && typeof handleChange.cancel === 'function') {
    handleChange.cancel();
  }
  const f = 'OEMNumber';
  const editingActive = editing.rowIdx === i && editing.field === f;
  let value = editingActive ? editing.value : rows[i]?.[f] || '';
  let trimmed = value.trim();
  const line = /^\d{1,3}$/.test(trimmed) ? parseInt(trimmed, 10) : null;
  const fitKey = rows[i]?.FitmentKey;
  if (!fitKey) return;

  if (!extractedPerFitment[fitKey]) {
    setLoading(true);
    try {
      const { data: fiches } = await axios.get(`/api/fitments/${fitKey}/fiches`);
      const agg = {};
      fiches.forEach((fiche) => {
        const ext = JSON.parse(fiche.ExtractedData || '{}');
        Object.entries(ext).forEach(([l, v]) => {
          const arr = Array.isArray(v) ? v : v ? [v] : [];
          agg[l] = (agg[l] || []).concat(
            arr.map((p) => ({ ...p, ficheKey: fiche.FicheImageKey, screenshotPath: fiche.ScreenshotPath }))
          );
        });
      });
      setExtractedPerFitment((prev) => ({ ...prev, [fitKey]: agg }));
    } catch (err) {
      console.error('Failed to load fiches for OEM lookup:', err);
    } finally {
      setLoading(false);
    }
  }

  const agg = extractedPerFitment[fitKey];
  if (!agg) return;

  if (line !== null) {
    const group = agg[line.toString()] || [];
    const previousValue = rows[i]?.[f] || '';

    if (group.length === 0) {
      // Revert to previous and save as normal OEM
      if (editingActive) {
        setEditing({ rowIdx: i, field: f, value: previousValue });
      } else {
        setRows((prev) =>
          prev.map((r, idx) => (idx === i ? { ...r, OEMNumber: previousValue } : r))
        );
      }
      handleBlur(i, f);
      return;
    }

    if (group.length === 1) {
      const part = group[0];
      setRows((prev) =>
        prev.map((r, idx) =>
          idx === i ? { ...r, OEMNumber: part.oem || '', Quantity: part.qty || '1' } : r
        )
      );
      try {
        await axios.patch(`/api/fitments/${fitKey}`, { field: 'OEMNumber', value: part.oem || '' });
        await axios.patch(`/api/fitments/${fitKey}`, { field: 'Quantity', value: part.qty || '1' });
        // Refetch full row to update SKUGroup/OEMNote
        const refetchRes = await axios.get(`/api/fitments/${fitKey}`);
        const updatedRow = refetchRes.data;
        setRows((prev) =>
          prev.map((r, idx) =>
            idx === i ? { ...r, ...updatedRow } : r
          )
        );
      } catch (patchErr) {
        console.error('Failed to patch OEMNumber/Quantity:', patchErr);
      }
      if (editingActive) setEditing({ rowIdx: null, field: '', value: '' });
    } else {
      // Revert to previous to avoid saving line
      if (editingActive) {
        setEditing({ rowIdx: i, field: f, value: previousValue });
      } else {
        setRows((prev) =>
          prev.map((r, idx) => (idx === i ? { ...r, OEMNumber: previousValue } : r))
        );
      }
      setOemChoices(group);
      setOemPopupRowIdx(i);
      setOemPopupLine(line);
      setOemPopupVisible(true);
    }
  } else {
    // Non-line: Format if toggle on and type not Misc/NonOEM
    if (formatToggle && trimmed) {
      const type = await resolveOEMTypeFast(trimmed);
      if (type !== 'Misc' && type !== 'NonOEM') {
        try {
          const manufacturer = rows[i]?.Make || 'Honda'; // Fallback to Honda if no Make
          const { data: { formatted } } = await axios.post('/api/format-oem', {
            oem: trimmed,
            manufacturer
          });
          value = formatted;
        } catch (formatErr) {
          console.error('Failed to format OEM:', formatErr);
          // Fall back to original on error
        }
      }
    }
    // Update editing value if active, then blur to commit
    if (editingActive) {
      setEditing({ rowIdx: i, field: f, value });
    } else {
      setRows((prev) =>
        prev.map((r, idx) => (idx === i ? { ...r, OEMNumber: value } : r))
      );
    }
    handleBlur(i, f);
  }
};

useEffect(() => {
  if (dataLoaded) {
    setIsPending(false);
    setPage(1);
    // Add min length check to prevent empty/partial filters triggering no results
    const hasSufficientFilters = Object.values(filters).some(val => val.length >= 3);
    if (hasSufficientFilters) {
      loadOverview(false);
    }
  }
}, [debouncedFilters]);

// ─── Inline edit commit ────────────────────
const commitEdit = (rowIdx, field, valueOverride = null) => {
  const row = rows[rowIdx];
  const fitmentKey = row.FitmentKey;
  const editValue = valueOverride !== null ? valueOverride : editing.value;
  if (scrollRef.current) { scrollPositionRef.current = scrollRef.current.scrollTop; }
  setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [field]: editValue } : r));
  (async () => {
    try {
      let apiRes;
      if (!fitmentKey) {
        const createKey = `${row.ModelKey}-${row.ItemTypeKey}-${row.FitmentLocationKey}`;
        if (createInFlightRef.current.has(createKey)) { console.warn('Create in-flight for', createKey); return; }
        createInFlightRef.current.add(createKey);
        apiRes = await axios.post(`/api/fitments`, {
          modelKey: row.ModelKey,
          fitmentLocationKey: row.FitmentLocationKey,
          itemTypeKey: row.ItemTypeKey,
          field, value: editValue || null
        });
      } else {
        apiRes = await axios.patch(`/api/fitments/${fitmentKey}`, { field, value: editValue || null });
      }
      const updated = apiRes.data.updated;
      setRows(prev => prev.map((r, i) => {
        if (i !== rowIdx) return r;
        const merged = { ...r, ...updated };
        if (field === 'OEMNumber') {
          const nextOem = typeof merged.OEMNumber === 'string' ? merged.OEMNumber.trim() : '';
          const responseIsKit = updated?.IsKit;
          const normalizedIsKit = responseIsKit != null
            ? (responseIsKit === true || responseIsKit === 1 || responseIsKit === '1')
            : nextOem.toUpperCase().startsWith(KIT_ID_PREFIX);
          merged.IsKit = normalizedIsKit;
          const responseKitId = updated?.KitId ?? updated?.KitID;
          const resolvedKitId = responseKitId || (normalizedIsKit ? nextOem : null);
          merged.KitId = resolvedKitId || null;
          merged.KitID = resolvedKitId || null;
        }
        const rfSet = new Set(merged.RecentFields ? merged.RecentFields.split(',').map(x => x.trim()) : []);
        rfSet.add(field);
        merged.RecentFields = Array.from(rfSet).join(',');
        if (!fitmentKey) { merged.DateCreated = new Date().toISOString(); }
        return merged;
      }));
    } catch (err) {
      console.error(`${fitmentKey ? 'Update' : 'Create'} failed`, err);
      alert(err?.response?.data?.error || 'Save failed—see console.');
      setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [field]: row[field] } : r));
    } finally {
      setEditing({ rowIdx: null, field: '', value: '' });
      if (!fitmentKey) {
        const createKey = `${row.ModelKey}-${row.ItemTypeKey}-${row.FitmentLocationKey}`;
        createInFlightRef.current.delete(createKey);
      }
    }
  })();
};

// Loads from storage on mount
useEffect(() => {
  try {
    const prefs = localStorage.getItem(COL_PREF_KEY);
    if (prefs) {
      const { order, widths } = JSON.parse(prefs);
      const sanitizedOrder = ensureColumnCoverage(order);
      setColumnOrder(sanitizedOrder);
      setColumnWidths(widths || {});
      setPrevOrder([...sanitizedOrder]); // Deep copy
      setPrevWidths({ ...(widths || {}) }); // Deep copy
      console.log('Loaded column prefs from storage:', { order: sanitizedOrder, widths });
    } else {
      console.log('No column prefs found in storage.');
    }
  } catch (err) {
    console.error('Failed to load/parse column prefs:', err);
    // Fall back to defaults on error
  } finally {
    setIsInitialLoad(false);
  }
}, []);

// Auto-save on column order/width changes (skip initial load)
useEffect(() => {
  if (isInitialLoad) return;
  debouncedSavePrefs();
}, [columnOrder, columnWidths, isInitialLoad]);

// Flush save on component unmount (e.g., page close)
useEffect(() => {
  return () => {
    debouncedSavePrefs.flush();
    console.log('Flushed column prefs save on unmount.');
  };
}, []);

// Cache state after loads/edits
useEffect(() => {
  if (dataLoaded && !cacheLoaded) {
    const cacheData = {
      rows,
      totalCount,
      page,
      filters,
      selectedGroup,
      selectedSubGroup,
      timestamp: Date.now()
    };

    try {
      const payload = JSON.stringify({ data: cacheData });
      const estimatedBytes = typeof Blob !== 'undefined'
        ? new Blob([payload]).size
        : payload.length * 2; // Fallback estimate (UTF-16 chars)

      if (estimatedBytes > CACHE_SIZE_LIMIT_BYTES) {
        console.warn(
          `Skipping dataCollection cache write – payload ${estimatedBytes.toLocaleString()} bytes exceeds limit.`
        );
        localStorage.removeItem(CACHE_KEY);
      } else {
        localStorage.setItem(CACHE_KEY, payload);
      }
    } catch (err) {
      if (err && (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
        console.warn('Local storage quota exceeded while caching data collection grid. Clearing cached copy.');
        try {
          localStorage.removeItem(CACHE_KEY);
        } catch (clearErr) {
          console.warn('Failed to clear oversized cache entry', clearErr);
        }
      } else {
        console.error('Failed to persist data collection cache', err);
      }
    } finally {
      setCacheLoaded(true);
    }
  }
}, [rows, totalCount, page, filters, selectedGroup, selectedSubGroup, dataLoaded]);

// Optional: Clear cache on unmount or manual refresh
useEffect(() => {
  return () => {
    // Optionally clear on component unmount, but keep for tab restore
  };
}, []);

useVisibilityChange(isVisible => {
  if (isVisible) {
    // Optional: Auto-refresh or other logic when tab becomes visible
    console.log('Tab visible - could auto-refresh here');
  } else {
    console.log('Tab hidden');
  }
});

//Unique Model Bulk Update
useEffect(() => {
  const modelMap = new Map();
  rows.forEach(r => {
    const label = `${r.Make} ${r.ModelName} ${r.Year}`;
    if (!modelMap.has(r.ModelKey)) {
      modelMap.set(r.ModelKey, label);
    }
  });
  const sorted = Array.from(modelMap).map(([key, label]) => ({ key, label }));
  setUniqueModels(sorted);
  if (sorted.length > 0 && !selectedModelKey) {
    setSelectedModelKey(sorted[0].key); // Auto-select first if none
  }
}, [rows]);
//Unique Model Bulk Update, select current model
useEffect(() => {
  if (focusPos.row >= 0 && focusPos.row < rows.length) {
    const currentRow = rows[focusPos.row];
    if (!currentRow) return; // Added: Safety skip if row undefined
    const currentModelKey = currentRow.ModelKey;
    if (currentModelKey !== selectedModelKey) {
      setSelectedModelKey(currentModelKey);
    }
  }
}, [focusPos.row, rows]);

//Bulk Redo/Undo
useEffect(() => {
  const stored = localStorage.getItem('lastBulkFill');
  if (stored) {
    setLastBulkChanges(JSON.parse(stored));
    setIsUndone(false); // Assume not undone on load
  }
}, []);

// Restore scroll position after rows update
useLayoutEffect(() => {
  if (scrollRef.current && isAppending) {
    setTimeout(() => {
      scrollRef.current.scrollTop = scrollPositionRef.current;
    }, 0);
    setIsAppending(false);
  }
}, [rows, isAppending]);

//Auto-Collapse header
useEffect(() => {
  if (saveStatus === 'saved') {
    setIsLayoutCollapsed(true);
  }
}, [saveStatus]);

useEffect(() => {
  if (selectedGroup && selectedSubGroup) {
    setIsGroupCollapsed(true);
  }
}, [selectedGroup, selectedSubGroup]);
  // ─── Handle input change ───────────────────
  const handleChange = (rowIdx, field, value) => {
    setEditing({ rowIdx, field, value });
  };
  const handleBlur = (rowIdx, field) => {
    if (editing.rowIdx === rowIdx && editing.field === field) {
      const _val = editing.value; setEditing({ rowIdx: null, field: '', value: '' });
      commitEdit(rowIdx, field, _val);
    }
  };


const handleKeyDown = async (e, rowIdx, field, colIdx) => {
  const key = `${rowIdx}-${colIdx}`;
  if (e.key === 'Enter') {
    const _val = editing.value; setEditing({ rowIdx: null, field: '', value: '' });
    commitEdit(rowIdx, field, _val);
  }
  if (e.key.startsWith('Arrow') || e.key === 'Tab') {
    e.preventDefault();
    e.stopPropagation(); // Added: Prevent bubble
    let dRow = 0, dCol = 0;
    switch (e.key) {
      case 'ArrowUp': dRow = -1; break;
      case 'ArrowDown': 
        dRow = 1; 
        if (rowIdx === rows.length - 1 && hasMore && !loading) {
          if (scrollRef.current) {
            scrollPositionRef.current = scrollRef.current.scrollTop;
          }
          setLoading(true);
          setPage(p => p + 1);
          loadOverview(true);
        }
        break;
      case 'ArrowLeft': dCol = -1; break;
      case 'ArrowRight': dCol = 1; break;
            case 'Tab': {
        e.preventDefault();
        e.stopPropagation();

        // Dynamic col indices (unchanged)
        const oemCol = columnOrder.indexOf('OEMNumber');
        const qtyCol = columnOrder.indexOf('Quantity');
        if (oemCol === -1 || qtyCol === -1) return;

        // Snapshot of the row BEFORE committing
        let currentRow = rows[rowIdx];

        // Detect whether OEMNumber changed (your original logic kept)
        let wasChanged = false;
        let originalValue;
        if (editing.rowIdx === rowIdx && editing.field === field && field === 'OEMNumber') {
          originalValue = currentRow[field] ?? '';
          wasChanged = editing.value !== originalValue && editing.value.trim() !== '';
        }

        // Helper to find the next empty OEMNumber cell
        const findNextEmptyOEM = (startRow, startCol) => {
          let tRow = startRow;
          let tCol = startCol;
          while (tRow < rows.length) {
            for (; tCol < columnOrder.length; tCol++) {
              const tField = columnOrder[tCol];
              if (tField === 'OEMNumber') {
                const r = rows[tRow];
                const isEmpty = !r.OEMNumber;
                if (isEmpty && !(tRow === rowIdx && tCol === colIdx)) {
                  return { row: tRow, col: tCol };
                }
              }
            }
            tRow += 1;
            tCol = 0;
          }
          return null;
        };

        let target = null;

        // --- NEW: decide using the *typed* value’s OEMType (don’t wait for save) ---
        if (field === 'OEMNumber' && wasChanged) {
          const typed = (editing.value || '').trim();
          // Look up OEMType for the typed value; quick and cached
          const oemType = await resolveOEMTypeFast(typed);

          // Commit in the background so focus feels instant
          // (don’t await, to avoid slowing navigation)
          const __v1 = editing.value; setEditing({ rowIdx: null, field: '', value: '' }); commitEdit(rowIdx, field, __v1);

          if (oemType && String(oemType).toLowerCase() === 'misc') {
            // Skip Quantity -> jump to next empty OEMNumber
            target = findNextEmptyOEM(rowIdx + 1, 0);
          } else {
            // Non-Misc (or unknown) -> go to Quantity in same row
            target = { row: rowIdx, col: qtyCol };
          }
        } else if (field === 'OEMNumber') {
          // OEMNumber not changed or empty: use your original default behavior
          if (editing.rowIdx === rowIdx && editing.field === field) {
            const __v2 = editing.value; setEditing({ rowIdx: null, field: '', value: '' }); await commitEdit(rowIdx, field, __v2);
          }
          currentRow = rows[rowIdx];
          if (currentRow.OEMNumber && String(currentRow.OEMType || '').toLowerCase() !== 'misc') {
            target = { row: rowIdx, col: qtyCol };
          } else {
            target = findNextEmptyOEM(rowIdx, colIdx + 1);
          }
        } else if (field === 'Quantity') {
          // From Quantity -> next empty OEMNumber on next row (unchanged)
          if (editing.rowIdx === rowIdx && editing.field === field) {
            const __v3 = editing.value; setEditing({ rowIdx: null, field: '', value: '' }); await commitEdit(rowIdx, field, __v3);
          }
          target = findNextEmptyOEM(rowIdx + 1, 0);
        } else {
          // Any other editable field (SKUGroup/OEMNote/AdditionalFitmentInfo)
          if (editing.rowIdx === rowIdx && editing.field === field) {
            const __v4 = editing.value; setEditing({ rowIdx: null, field: '', value: '' }); await commitEdit(rowIdx, field, __v4);
          }
          target = findNextEmptyOEM(rowIdx, colIdx + 1);
        }

        // Load more if needed and we ran out of targets
        if (!target && hasMore && !loading) {
          if (scrollRef.current) {
            scrollPositionRef.current = scrollRef.current.scrollTop;
          }
          setLoading(true);
          setPage(p => p + 1);
          loadOverview(true);
          return;
        }

        if (target) {
          const nextKey = `${target.row}-${target.col}`;
          setTimeout(() => {
            const nextInput = inputRefs.current[nextKey];
            if (nextInput && document.activeElement !== nextInput) { // Skip if already focused
              nextInput.focus();
            }
          }, 100); // Buffer for render/update
        }
        return; // ← stop Tab from falling into the Arrow fallback

      }


      default: return;
    }
    if (e.key === 'Tab') return; // ← prevent Tab from running Arrow fallback
    // For Arrows: Standard move (unchanged)
    let tRow = rowIdx + dRow;
    let tCol = colIdx + dCol;
  if (tRow < 0 || tRow >= rows.length || tCol < 0 || tCol >= columnOrder.length) return;
    const targetField = columnOrder[tCol];
    if (!['SKUGroup','OEMNumber','Quantity','OEMNote','AdditionalFitmentInfo'].includes(targetField)) return;
    const nextKey = `${tRow}-${tCol}`;
    setTimeout(() => {
      inputRefs.current[nextKey]?.focus();
    }, 0);
  }
  if (e.key === 'Escape') {
    setEditing({ rowIdx: null, field: '', value: '' });
  }
};



// -- Auto-load for Sub-Group
useEffect(() => {
  if (selectedGroup && selectedSubGroup) {
    if (dataLoaded) return; // Added: Skip if already loaded
    const timer = setTimeout(() => {
      setDataLoaded(true);
      setPage(1);
      loadOverview(false);
    }, 300); // Debounce for smooth select
    return () => clearTimeout(timer);
  }
}, [selectedGroup, selectedSubGroup]);



  // ─── Row/col highlighting ───────────────────
  const onFocusCell = (rowIdx, colIdx) => {
    setFocusPos({ row: rowIdx, col: colIdx });
  };

  // ─── Filtering ──────────────────────────────
const handleFilterChange = (e, key) => {
  const newValue = e.target.value;
  setFilters(f => ({ ...f, [key]: newValue }));
  setPage(1);
  if (newValue.length >= 3) {
    setIsPending(true);
  }
};


  // ─── History actions ──────────────────────────────────────
  const handleRevert = async entry => {
    if (!canDataCollectionWrite) {
      window.alert('You do not have permission to revert history entries.');
      return;
    }
    const ok = window.confirm(
      `Revert "${entry.FieldName}" on ${entry.Make} ${entry.ModelName} ${entry.Year} (${entry.ItemType})?\n` +
      `from "${entry.NewValue}" back to "${entry.OldValue}"?`
    );
    if (!ok) return;
    await axios.patch(
      `/api/fitments/${entry.FitmentKey}`,
      { field: entry.FieldName, value: entry.OldValue }
    );
    loadOverview(false); // Refresh grid to reflect changes
    openHistory(entry.FitmentKey); // Reload history
  };

  const handleRedo = async entry => {
    if (!canDataCollectionWrite) {
      window.alert('You do not have permission to re-apply history entries.');
      return;
    }
    const ok = window.confirm(
      `Re-apply "${entry.FieldName}" on ${entry.Make} ${entry.ModelName} ${entry.Year} (${entry.ItemType})?\n` +
      `to "${entry.NewValue}"?`
    );
    if (!ok) return;
    await axios.patch(
      `/api/fitments/${entry.FitmentKey}`,
      { field: entry.FieldName, value: entry.NewValue }
    );
    loadOverview(false); // Refresh grid
    openHistory(entry.FitmentKey); // Reload history
  };

  const openHistory = async fitmentKey => {
    setHistoryFitmentKey(fitmentKey);
    try {
      const res = await axios.get(
        `/api/fitments/history?fitmentKey=${fitmentKey}`
      );
      setHistoryRecords(res.data);
      setHistoryVisible(true);
    } catch (err) {
      console.error('Failed to fetch history', err);
      alert('Failed to load history—see console.');
    }
  };

  const closeHistory = () => setHistoryVisible(false);

  // ─── Reload Layout from Storage ────────────
  const reloadLayout = () => {
    try {
      const prefs = localStorage.getItem(COL_PREF_KEY);
      if (prefs) {
        const { order, widths } = JSON.parse(prefs);
        const sanitizedOrder = ensureColumnCoverage(order);
        setColumnOrder(sanitizedOrder);
        setColumnWidths(widths || {});
        setPrevOrder([...sanitizedOrder]);
        setPrevWidths({ ...(widths || {}) });
        console.log('Reloaded column prefs from storage:', { order: sanitizedOrder, widths });
      } else {
        console.log('No saved column prefs to reload.');
      }
    } catch (err) {
      console.error('Failed to reload/parse column prefs:', err);
    }
  };

const handleBulkFill = async () => {
  if (!selectedModelKey || !fillValue) {
    alert('Select a model and fill value first.');
    return;
  }
  const value = fillValue === 'Custom' ? customFillValue.trim() : fillValue;
  if (fillValue === 'Custom' && !value) {
    alert('Enter a custom value.');
    return;
  }
  const modelRows = rows.filter(r => r.ModelKey === selectedModelKey && !r.OEMNumber);
  if (modelRows.length === 0) {
    alert('No empty OEMNumber cells for this model in displayed rows.');
    return;
  }
  const modelLabel = uniqueModels.find(m => m.key === selectedModelKey)?.label || 'selected model';
  if (!window.confirm(`Fill ${modelRows.length} empty OEMNumber cells for ${modelLabel} with "${value}"? Only displayed rows.`)) {
    return;
  }
  const changes = [];
  for (let i = 0; i < modelRows.length; i++) {
    const rowIdx = rows.findIndex(r => r === modelRows[i]);
    const row = modelRows[i];
    const oldValue = row.OEMNumber || null;
    const wasNew = !row.FitmentKey;
    let fitmentKey = row.FitmentKey;
    try {
      let apiRes;
      if (wasNew) {
        // Create new
        apiRes = await axios.post('/api/fitments', {
          modelKey: row.ModelKey,
          fitmentLocationKey: row.FitmentLocationKey,
         itemTypeKey: row.ItemTypeKey,          // ← NEW
          field: 'OEMNumber',
          value: value || null
        });
        fitmentKey = apiRes.data.updated.FitmentKey;
      } else {
        // Update
        apiRes = await axios.patch(`/api/fitments/${fitmentKey}`, { field: 'OEMNumber', value: value || null });
      }
      const updated = apiRes.data.updated;
      // Update local row
      setRows(prev => prev.map((r, idx) => idx === rowIdx ? { ...r, ...updated, DateCreated: wasNew ? new Date().toISOString() : r.DateCreated } : r));
      // Add to RecentFields
      setRows(prev => prev.map((r, idx) => {
        if (idx !== rowIdx) return r;
        const rfSet = new Set(r.RecentFields ? r.RecentFields.split(',').map(x => x.trim()) : []);
        rfSet.add('OEMNumber');
        return { ...r, RecentFields: Array.from(rfSet).join(',') };
      }));
      changes.push({ rowIdx, fitmentKey, oldValue, newValue: value, wasNew, modelKey: row.ModelKey, fitmentLocationKey: row.FitmentLocationKey });
    } catch (err) {
      console.error('Bulk fill failed for row', rowIdx, err);
      alert('Bulk fill partial fail—see console.');
    }
    await new Promise(resolve => setTimeout(resolve, 100)); // Rate limit
  }
  setLastBulkChanges(changes);
  setIsUndone(false);
  localStorage.setItem('lastBulkFill', JSON.stringify(changes));
  alert('Fill complete.');
};

const handleUndoLastFill = async () => {
  if (!lastBulkChanges.length || isUndone) {
    alert('No last fill to undo.');
    return;
  }
  if (!window.confirm(`Undo last bulk fill on ${lastBulkChanges.length} cells?`)) return;
  const reversedChanges = lastBulkChanges.slice().reverse();
  const remainingChanges = [];
  for (const change of reversedChanges) {
    try {
      console.log('Undoing change:', change);
      if (change.field === 'OEMNumber' && change.wasNew && change.oldValue === null) {
        console.log('Deleting fitmentKey:', change.fitmentKey);
        await axios.delete(`/api/fitments/${change.fitmentKey}`);
        setRows(prev => prev.map((r, idx) => {
          if (idx !== change.rowIdx) return r;
          return { 
            ...r, 
            FitmentKey: null, 
            OEMNumber: null, 
            SKUGroup: null,
            Quantity: null,
            OEMNote: null,
            AdditionalFitmentInfo: null,
            RecentFields: '', 
            DateCreated: null 
          };
        }));
        remainingChanges.push(change);
      } else {
        console.log('Patching back to oldValue:', change.oldValue);
        await axios.patch(`/api/fitments/${change.fitmentKey}`, { field: change.field, value: change.oldValue });
        setRows(prev => prev.map((r, idx) => idx === change.rowIdx ? { ...r, [change.field]: change.oldValue } : r));
        setRows(prev => prev.map((r, idx) => {
          if (idx !== change.rowIdx) return r;
          const rfSet = new Set(r.RecentFields ? r.RecentFields.split(',').map(x => x.trim()) : []);
          rfSet.add(change.field);
          return { ...r, RecentFields: Array.from(rfSet).join(',') };
        }));
        remainingChanges.push(change);
      }
    } catch (err) {
      console.error('Undo failed for', change.fitmentKey, change.field, err);
      alert('Undo partial fail—see console.');
    }
  }
  setLastBulkChanges(remainingChanges.reverse());
  localStorage.setItem('lastBulkFill', JSON.stringify(remainingChanges));
  setIsUndone(true);
  loadOverview(false); // Force refresh to sync visual
};

const handleRedoLastFill = async () => {
  if (!lastBulkChanges.length || !isUndone) {
    alert('No undone fill to redo.');
    return;
  }
  if (!window.confirm(`Redo last bulk fill on ${lastBulkChanges.length} cells?`)) return;
  for (const change of lastBulkChanges) {
    try {
      console.log('Redoing:', change);
      await axios.patch(`/api/fitments/${change.fitmentKey}`, { field: change.field, value: change.newValue });
      setRows(prev => prev.map((r, idx) => idx === change.rowIdx ? { ...r, [change.field]: change.newValue } : r));
      setRows(prev => prev.map((r, idx) => {
        if (idx !== change.rowIdx) return r;
        const rfSet = new Set(r.RecentFields ? r.RecentFields.split(',').map(x => x.trim()) : []);
        rfSet.add(change.field);
        return { ...r, RecentFields: Array.from(rfSet).join(',') };
      }));
    } catch (err) {
      console.error('Redo failed for', change.fitmentKey, change.field, err);
      alert('Redo partial fail—see console.');
    }
  }
  setIsUndone(false);
  loadOverview(false); // Refresh grid from DB
};

// Remove row (ghost ⇒ local remove; persisted ⇒ DELETE)
const handleRemoveOneOffRow = async (row) => {
  const label = `${row.Make} ${row.ModelName} ${row.Year} — ${row.ItemType}/${row.FitmentLocation}`;
  const ok = window.confirm(`Remove this one-off row?\n${label}`);
  if (!ok) return;

  if (!row.FitmentKey) {
    // ghost: just drop
    setRows(prev => prev.filter(r => !(r.ModelKey === row.ModelKey && r.FitmentLocationKey === row.FitmentLocationKey)));
    return;
  }
  try {
    await axios.delete(`/api/fitments/${row.FitmentKey}`);
    setRows(prev => prev.filter(r => r.FitmentKey !== row.FitmentKey));
  } catch (err) {
    console.error(err);
    alert('Delete failed — see console.');
  }
};


const handleCopyFromModel = async () => {
  if (!copySourceModelKey || !selectedModelKey) {
    alert('Select source and target models first.');
    return;
  }
  if (copySourceModelKey === selectedModelKey) {
    alert('Source and target must be different models.');
    return;
  }
  const selectedFields = Object.keys(copyFields).filter(f => copyFields[f]);
  if (selectedFields.length === 0 && !true) { // OEM always, but alert if no extras
    alert('At least OEMNumber will be copied, but select additional fields if needed.');
  }
  const sourceRows = rows.filter(r => r.ModelKey === copySourceModelKey);
  if (sourceRows.length === 0) {
    alert('No data in source model—load it first.');
    return;
  }
  const targetRows = rows.filter(r => r.ModelKey === selectedModelKey);
  const affected = targetRows.reduce((count, t) => {
    const s = sourceRows.find(sr => sr.ItemType === t.ItemType && sr.FitmentLocation === t.FitmentLocation);
    if (s && s.OEMNumber) {
      count += 1; // OEMNumber always
      count += selectedFields.filter(f => !copyOnlyEmpty || t[f] == null).length;
    }
    return count;
  }, 0);
  if (affected === 0) {
    alert('No cells to copy (no matching sources with OEMNumber or all targets non-empty).');
    return;
  }
  const sourceLabel = uniqueModels.find(m => m.key === copySourceModelKey)?.label || 'source';
  if (!window.confirm(`Copy up to ${affected} cells from ${sourceLabel} to current model? (Matches by ItemType/FitmentLocation; ${copyOnlyEmpty ? 'only empty targets' : 'overwrites all'}).`)) {
    return;
  }
  const changes = [];
  let processed = 0;
  for (const tRow of targetRows) {
    const sRow = sourceRows.find(sr => sr.ItemType === tRow.ItemType && sr.FitmentLocation === tRow.FitmentLocation);
    if (!sRow || !sRow.OEMNumber) continue;
    const rowIdx = rows.findIndex(r => r === tRow);
    const fieldsToCopy = ['OEMNumber', ...selectedFields];
    let fitmentKey = tRow.FitmentKey;
    let isNew = !fitmentKey;
    let apiRes;
    let recentFieldsSet = new Set(tRow.RecentFields ? tRow.RecentFields.split(',').map(x => x.trim()) : []);
    // Optimistic updates for all fields
    fieldsToCopy.forEach(f => {
      const val = sRow[f] ?? null;
      if (copyOnlyEmpty && tRow[f] != null) return;
      setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [f]: val } : r));
      recentFieldsSet.add(f);
    });
    try {
      for (const f of fieldsToCopy) {
        const val = sRow[f] ?? null;
        if (copyOnlyEmpty && tRow[f] != null) continue;
        if (!fitmentKey) {
          if (f !== 'OEMNumber') continue; // Create only on OEMNumber
          apiRes = await axios.post('/api/fitments', {
            modelKey: tRow.ModelKey,
            fitmentLocationKey: tRow.FitmentLocationKey,
            itemTypeKey: tRow.ItemTypeKey,        // ← NEW
            field: f,
            value: val
          });
          fitmentKey = apiRes.data.updated.FitmentKey;
          setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, FitmentKey: fitmentKey, DateCreated: new Date().toISOString() } : r));
        } else {
          apiRes = await axios.patch(`/api/fitments/${fitmentKey}`, { field: f, value: val });
        }
        changes.push({ rowIdx, fitmentKey, field: f, oldValue: tRow[f] ?? null, newValue: val, wasNew: isNew, modelKey: tRow.ModelKey, fitmentLocationKey: tRow.FitmentLocationKey });
        processed++;
        setCopyProgress((processed / affected) * 100);
      }
      // Full refresh after all fields for this row
      const fullRes = await axios.get(`/api/fitments/${fitmentKey}`);
      const fullUpdated = fullRes.data;
      setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, ...fullUpdated } : r));
      // Apply recent fields
      setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, RecentFields: Array.from(recentFieldsSet).join(',') } : r));
    } catch (err) {
      console.error('Copy failed for row', rowIdx, err);
      alert('Copy partial fail—see console.');
    }
  }
  setLastBulkChanges(changes);
  setIsUndone(false);
  localStorage.setItem('lastBulkFill', JSON.stringify(changes));
  setCopyProgress(0);
  alert('Copy complete.');
};

const openAddOneOff = () => {
  if (focusPos?.row == null || focusPos.row < 0 || focusPos.row >= rows.length) {
    alert('Click a row first to choose a Model & ItemType context.');
    return;
  }
  if (!selectedGroup) {
    alert('Pick a Product Group before adding one-offs.');
    return;
  }
  const baseRow = rows[focusPos.row];
  if (!baseRow?.ItemTypeKey || !baseRow?.ModelKey) {
    alert('Missing ItemType or Model context. Try reloading and selecting the row again.');
    return;
  }
  const existingKeys = Array.from(new Set(
    rows
      .filter(r => r.ModelKey === baseRow.ModelKey && r.ItemTypeKey === baseRow.ItemTypeKey)
      .map(r => r.FitmentLocationKey)
      .filter(key => key != null)
  ));
  setOneOffContext({
    baseRow,
    productGroupKey: selectedGroup,
    subProductGroupKey: selectedSubGroup || null,
    modelKey: baseRow.ModelKey,
    existingKeys
  });
};

const addGhostOneOffRow = (sourceRow, modelCtx, itemCtx, locationCtx) => {
  if (!modelCtx || !itemCtx || !locationCtx) return;
  const insertModelKey = modelCtx.ModelKey;
  const insertItemTypeKey = itemCtx.ItemTypeKey;
  const insertLocationKey = locationCtx.FitmentLocationKey;
  if (!insertModelKey || !insertItemTypeKey || !insertLocationKey) return;

  const defaultRow = sourceRow || {};
  const ghost = {
    ...defaultRow,
    ModelKey: insertModelKey,
    Make: modelCtx.Make,
    ModelName: modelCtx.ModelName,
    StreetName: modelCtx.StreetName,
    Year: modelCtx.Year,
    Region: modelCtx.Region,
    ItemTypeKey: insertItemTypeKey,
    ItemType: itemCtx.ItemType,
    FitmentLocationKey: insertLocationKey,
    FitmentLocation: locationCtx.FitmentLocation || locationCtx.name || defaultRow.FitmentLocation || '',
    LocSortOrder: locationCtx.SortOrder ?? null,
    ModelSortOrder: modelCtx.ModelSortOrder ?? defaultRow.ModelSortOrder ?? 0,
    ProductGroupKey: selectedGroup,
    SubProductGroupKey: selectedSubGroup || defaultRow.SubProductGroupKey || null,
    OneOffGroupKey: locationCtx.OneOffGroupKey ?? defaultRow.OneOffGroupKey ?? null,
    FitmentKey: null,
    SKUGroup: '',
    OEMNumber: '',
    Quantity: null,
    OEMNote: '',
    AdditionalFitmentInfo: '',
    FicheURL: '',
    RecentFields: '',
    DateCreated: null,
    IsGhostOneOff: true,
    isGhost: true
  };

  setRows(prev => {
    const next = [...prev];
    const siblings = next
      .map((row, idx) => ({ row, idx }))
      .filter(({ row }) => row.ModelKey === insertModelKey && row.ItemTypeKey === insertItemTypeKey);
    const insertionIndex = siblings.length
      ? siblings[siblings.length - 1].idx + 1
      : (() => {
          const sameModel = next
            .map((row, idx) => ({ row, idx }))
            .filter(({ row }) => row.ModelKey === insertModelKey);
          return sameModel.length ? sameModel[sameModel.length - 1].idx + 1 : next.length;
        })();
    next.splice(insertionIndex, 0, ghost);
    return next;
  });
};

const handleOneOffPromoted = (payload) => {
  if (!payload?.FitmentLocationKey || !payload?.ItemTypeKey) return;
  setAllOneOffsForSPG(prev => {
    const exists = prev.some(o =>
      o.FitmentLocationKey === payload.FitmentLocationKey &&
      o.ItemTypeKey === payload.ItemTypeKey
    );
    if (exists) return prev;
    return [...prev, payload];
  });
};

  const extractKitId = (entity) => {
    if (!entity) return null;
    const direct = entity.KitId ?? entity.KitID ?? entity.LinkedKitId;
    if (direct) return direct;
    const oem = entity.OEMNumber;
    if (typeof oem === 'string' && oem.toUpperCase().startsWith(KIT_ID_PREFIX)) {
      return oem;
    }
    return null;
  };

  const refreshFitmentRow = async (fitmentKey) => {
    if (!fitmentKey) return null;
    try {
      const { data } = await axios.get(`/api/fitments/${fitmentKey}`);
      if (data) {
        setRows(prev => prev.map(row => (row.FitmentKey === fitmentKey ? { ...row, ...data } : row)));
      }
      return data;
    } catch (err) {
      console.error('Failed to refresh fitment for kits:', err);
      return null;
    }
  };

  const closeKitModal = () => {
    const fitmentKey = kitModalState.fitment?.FitmentKey ?? null;
    setKitModalState({ open: false, kitId: null, fitment: null });
    if (fitmentKey) {
      refreshFitmentRow(fitmentKey);
    }
  };

  const ensureFitmentForKit = async (context) => {
    if (!context) return null;
    if (context.FitmentKey) {
      return { fitmentKey: context.FitmentKey, rowIndex: context.rowIndex ?? null };
    }

    const rowIndex = context.rowIndex ?? rows.findIndex((row) => row === context.baseRow);
    if (rowIndex < 0) {
      window.alert('Unable to locate the target row for this kit.');
      return null;
    }
    const sourceRow = rowIndex >= 0 ? rows[rowIndex] : null;

    if (!sourceRow || sourceRow.ModelKey == null || sourceRow.FitmentLocationKey == null || sourceRow.ItemTypeKey == null) {
      window.alert('Cannot create a kit row without model, item type, and fitment location information.');
      return null;
    }

    try {
      const { data } = await axios.post('/api/fitments', {
        modelKey: sourceRow.ModelKey,
        fitmentLocationKey: sourceRow.FitmentLocationKey,
        itemTypeKey: sourceRow.ItemTypeKey,
        field: 'OEMNumber',
        value: null
      });

      const fitment = data?.updated;
      if (fitment?.FitmentKey) {
        setRows(prev => prev.map((existing, idx) => {
          if (idx !== rowIndex) return existing;
          return {
            ...existing,
            ...fitment,
            IsGhost: false,
            isGhost: false,
            IsGhostOneOff: false,
            isGhostOneOff: false
          };
        }));

        setKitModalState(prev => {
          if (!prev.open) return prev;
          const nextFitment = prev.fitment ? { ...prev.fitment, FitmentKey: fitment.FitmentKey, rowIndex } : prev.fitment;
          return { ...prev, fitment: nextFitment };
        });

        return { fitmentKey: fitment.FitmentKey, rowIndex };
      }
    } catch (err) {
      console.error('Failed to create fitment for kit:', err);
      window.alert('Failed to create a fitment row for this kit. See console for details.');
    }

    return null;
  };

  const openKitModalForRow = async (row, rowIndexOverride = null) => {
    if (!canManageKits) {
      window.alert('You do not have permission to manage kits.');
      return;
    }

    const rowIndex = rowIndexOverride ?? rows.findIndex(r => r === row);
    const resolvedRow = rowIndex >= 0 ? rows[rowIndex] : row;
    let fitmentKey = resolvedRow?.FitmentKey ?? null;
    let latest = null;

    if (fitmentKey) {
      latest = await refreshFitmentRow(fitmentKey);
      fitmentKey = latest?.FitmentKey ?? fitmentKey;
    }

    const kitId = extractKitId(latest) ?? extractKitId(resolvedRow) ?? null;
    const fitmentContext = {
      FitmentKey: fitmentKey,
      ItemTypeKey: resolvedRow?.ItemTypeKey ?? null,
      ItemType: resolvedRow?.ItemType ?? '',
      FitmentLocationKey: resolvedRow?.FitmentLocationKey ?? null,
      FitmentLocation: resolvedRow?.FitmentLocation ?? '',
      OEMNumber: resolvedRow?.OEMNumber ?? '',
      SKUGroup: resolvedRow?.SKUGroup ?? '',
      ModelKey: resolvedRow?.ModelKey ?? null,
      Make: resolvedRow?.Make ?? '',
      ModelName: resolvedRow?.ModelName ?? '',
      Year: resolvedRow?.Year ?? null,
      Region: resolvedRow?.Region ?? null,
      Quantity: resolvedRow?.Quantity ?? null,
      OEMNote: resolvedRow?.OEMNote ?? '',
      rowIndex,
      baseRow: resolvedRow
    };

    setKitModalState({ open: true, kitId, fitment: fitmentContext });
  };

  const handleKitLinked = async (kitId, meta = {}) => {
    if (!kitId) return;

    const fitmentKey = meta.fitmentKey ?? kitModalState.fitment?.FitmentKey ?? null;
    if (!fitmentKey) return;

    const rowIndex = meta.ensureResult?.rowIndex ?? kitModalState.fitment?.rowIndex ?? rows.findIndex(row => row.FitmentKey === fitmentKey);
    const currentRow = rowIndex >= 0 ? rows[rowIndex] : rows.find(row => row.FitmentKey === fitmentKey);

    const quantityRaw = currentRow?.Quantity;
    const parsedQuantity = Number(quantityRaw);
    const quantityValue = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
    const existingNote = (currentRow?.OEMNote ?? '').toString().trim();
    const noteValue = existingNote.length > 0 ? existingNote : `Kit ${kitId}`;

    try {
      await axios.patch(`/api/fitments/${fitmentKey}`, { field: 'OEMNumber', value: kitId });
      if (quantityValue != null) {
        await axios.patch(`/api/fitments/${fitmentKey}`, { field: 'Quantity', value: quantityValue });
      }
      if (noteValue) {
        await axios.patch(`/api/fitments/${fitmentKey}`, { field: 'OEMNote', value: noteValue });
      }

      setRows(prev => prev.map((row, idx) => {
        if (row.FitmentKey !== fitmentKey) return row;
        return {
          ...row,
          FitmentKey: fitmentKey,
          OEMNumber: kitId,
          Quantity: quantityValue,
          OEMNote: noteValue,
          KitId: kitId,
          KitID: kitId,
          IsKit: true,
          IsGhost: false,
          isGhost: false,
          IsGhostOneOff: false,
          isGhostOneOff: false
        };
      }));

      await refreshFitmentRow(fitmentKey);

      setKitModalState(prev => {
        if (!prev.open) return prev;
        const nextFitment = prev.fitment ? { ...prev.fitment, FitmentKey: fitmentKey, rowIndex } : prev.fitment;
        return { ...prev, kitId, fitment: nextFitment };
      });
    } catch (err) {
      console.error('Failed to sync kit fitment details:', err);
      window.alert('Kit saved but failed to update the fitment row. See console for details.');
    }
  };


const handleAutoCopyFromYear = async (direction) => {
  const currentModel = uniqueModels.find(m => m.key === selectedModelKey);
  if (!currentModel) {
    alert('Select a target model first.');
    return;
  }
  // Parse the current model's label to extract Make, ModelName (combined middle parts), and Year
  const labelParts = currentModel.label.split(' ');
  const make = labelParts[0];  // First part is always Make
  const yearStr = labelParts.pop();  // Last part is Year (as string)
  const modelName = labelParts.slice(1).join(' ');  // Middle parts combined as ModelName (handles spaces in names)
  
  console.log('Parsed Current Model Label:', {  // Log parsing step for debug
    originalLabel: currentModel.label,
    make,
    modelName,
    yearStr
  });
  
  // Convert Year string to integer for arithmetic
  const currentYear = parseInt(yearStr, 10);  // Base 10 parse; truncates any non-numeric but assumes clean labels
  
  // Handle invalid parse (e.g., non-numeric Year in label)
  if (isNaN(currentYear)) {
    alert('Unable to parse year from label—check format.');
    return;
  }
  
  // This is the key line: Adjust year by -1 for 'prev' or +1 for 'next'
  const targetYear = currentYear + (direction === 'prev' ? -1 : 1);  // Simple ternary for direction; results in e.g., 2001 -> 2000 or 2002
  
  console.log('Calculated Target Year:', {  // Log adjustment step
    direction,
    currentYear,
    targetYear
  });
  const targetLabel = `${make} ${modelName} ${targetYear}`;
  let sourceKey;
  const selectedFields = Object.keys(copyFields).filter(f => copyFields[f]);
  const targetRows = rows.filter(r => r.ModelKey === selectedModelKey);
  const affectedEstimate = targetRows.length * (1 + selectedFields.length); // Rough
  if (!window.confirm(`Copy up to ${affectedEstimate} cells from ${targetLabel} to current model? (Matches by ItemType/FitmentLocation; ${copyOnlyEmpty ? 'only empty targets' : 'overwrites all'}).`)) {
    return;
  }

  const proceedWithCopy = async (sourceKey) => {
    const rowRes = await axios.get(`/api/data-collection-overview?pgKey=${selectedGroup}${selectedSubGroup ? `&spgKey=${selectedSubGroup}` : ''}&Make=${encodeURIComponent(make)}&ModelName=${encodeURIComponent(modelName)}&Year=${targetYear}`, { timeout: OVERVIEW_LOAD_TIMEOUT_MS });
    const sourceRows = rowRes.data.rows || [];
    if (sourceRows.length === 0) {
      alert('No data in source year—nothing to copy.');
      return;
    }
    const existingKeys = new Set(rows.map(r => `${r.ModelKey}-${r.FitmentLocationKey}`));
    const filteredNew = sourceRows.filter(nr => !existingKeys.has(`${nr.ModelKey}-${nr.FitmentLocationKey}`));
    setRows(prev => [...prev, ...filteredNew]);
    setUniqueModels(prev => {
      if (prev.some(m => m.key === sourceKey)) return prev;
      return [...prev, { key: sourceKey, label: targetLabel }];
    });
    const affected = targetRows.reduce((count, t) => {
      const s = sourceRows.find(sr => sr.ItemType === t.ItemType && sr.FitmentLocation === t.FitmentLocation);
      if (s && s.OEMNumber) {
        count += 1; // OEMNumber
        count += selectedFields.filter(f => !copyOnlyEmpty || t[f] == null).length;
      }
      return count;
    }, 0);
    if (affected === 0) {
      alert('No cells to copy.');
      return;
    }
    const changes = [];
    let processed = 0;
    for (const tRow of targetRows) {
      const sRow = sourceRows.find(sr => sr.ItemType === tRow.ItemType && sr.FitmentLocation === tRow.FitmentLocation);
      if (!sRow || !sRow.OEMNumber) continue;
      const rowIdx = rows.findIndex(r => r === tRow);
      const fieldsToCopy = ['OEMNumber', ...selectedFields];
      let fitmentKey = tRow.FitmentKey;
      let isNew = !fitmentKey;
      let apiRes;
      let recentFieldsSet = new Set(tRow.RecentFields ? tRow.RecentFields.split(',').map(x => x.trim()) : []);
      fieldsToCopy.forEach(f => {
        const val = sRow[f] ?? null;
        if (copyOnlyEmpty && tRow[f] != null) return;
        setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [f]: val } : r));
        recentFieldsSet.add(f);
      });
      try {
        for (const f of fieldsToCopy) {
          const val = sRow[f] ?? null;
          if (copyOnlyEmpty && tRow[f] != null) continue;
          if (!fitmentKey) {
            if (f !== 'OEMNumber') continue;
            apiRes = await axios.post('/api/fitments', {
              modelKey: tRow.ModelKey,
              fitmentLocationKey: tRow.FitmentLocationKey,
              itemTypeKey: tRow.ItemTypeKey,        // ← NEW
              field: f,
              value: val
            });
            fitmentKey = apiRes.data.updated.FitmentKey;
            setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, FitmentKey: fitmentKey, DateCreated: new Date().toISOString() } : r));
          } else {
            apiRes = await axios.patch(`/api/fitments/${fitmentKey}`, { field: f, value: val });
          }
          changes.push({ rowIdx, fitmentKey, field: f, oldValue: tRow[f] ?? null, newValue: val, wasNew: isNew, modelKey: tRow.ModelKey, fitmentLocationKey: tRow.FitmentLocationKey });
          processed++;
          setCopyProgress((processed / affected) * 100);
        }
        const fullRes = await axios.get(`/api/fitments/${fitmentKey}`);
        const fullUpdated = fullRes.data;
        setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, ...fullUpdated } : r));
        setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, RecentFields: Array.from(recentFieldsSet).join(',') } : r));
      } catch (err) {
        console.error('Copy failed for row', rowIdx, err);
        alert('Copy partial fail—see console.');
      }
    }
    setLastBulkChanges(changes);
    setIsUndone(false);
    localStorage.setItem('lastBulkFill', JSON.stringify(changes));
    setCopyProgress(0);
    alert('Copy complete.');
  };

  try {
    const res = await axios.get(`/api/models?make=${encodeURIComponent(make)}&modelName=${encodeURIComponent(modelName)}&year=${targetYear}`);
    if (res.data.length === 0) {
      alert(`No model found for year ${targetYear}.`);
      return;
    }
    const candidates = Array.isArray(res.data) ? res.data : [];
    if (candidates.length === 0) {
      console.error('Invalid res.data - not array or empty after coerce');
      alert('Failed to fetch valid models—see console.');
      return;
    }
    if (candidates.length > 1) {
      onModelSelectRef.current = proceedWithCopy;
      setCandidateModels(candidates);
      setShowModelSelector(true);
      return;
    }
    if (candidates[0].Year !== targetYear) {
      alert(`Model found but year mismatch (expected ${targetYear}, got ${candidates[0].Year}).`);
      return;
    }
    sourceKey = candidates[0].ModelKey;

    // Initial call if single
    proceedWithCopy(sourceKey);
  } catch (err) {
    alert('Failed to fetch year model—see console.');
    console.error(err);
  }
};

  // ─── Dynamic grid height calculation ─────────────────
  const recalcGridHeight = useCallback(() => {
    if (!scrollRef.current) return;
    const rect = scrollRef.current.getBoundingClientRect();
    const bottomBarHeight = bottomBarRef.current ? bottomBarRef.current.offsetHeight : 45;
    const bottomGutter = bottomBarHeight + 8;
    const available = Math.floor(window.innerHeight - rect.top - bottomGutter);
    setGridHeight(Math.max(200, available));
  }, []);

  useEffect(() => {
    recalcGridHeight();
    window.addEventListener('resize', recalcGridHeight);
    return () => window.removeEventListener('resize', recalcGridHeight);
  }, [recalcGridHeight]);

  // Recalculate when collapsible sections toggle or data changes
  useEffect(() => {
    const timer = setTimeout(recalcGridHeight, 50);
    return () => clearTimeout(timer);
  }, [isGroupCollapsed, isLayoutCollapsed, isCopyCollapsed, dataLoaded, recalcGridHeight]);

  return (
    <div style={{ padding: '0.5rem 1rem 0 1rem' }}>
      <h2 style={{ margin: '0 0 0.5rem 0' }}>Data Collection Overview</h2>
      {showModelSelector && (
        <div style={{
          position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--input-bg)', color: 'var(--text)', padding: '1rem', border: '1px solid #ddd', boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          zIndex: 1000, maxWidth: '80%', maxHeight: '60vh', overflow: 'auto'
        }}>
          <h3>Select Model to Copy From (Multiple Matches Found)</h3>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {['Make', 'Model', 'Year', 'ModelID', 'YearID', 'Region', 'EngineType', 'EngineCC', 'VehicleType'].map(col => (
                  <th key={col} style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {candidateModels.map((model, idx) => {
                const row = {
                  Make: model.Make,
                  Model: `${model.ModelName}${model.StreetName ? ` ${model.StreetName}` : ''}`,
                  Year: model.Year,
                  ModelID: model.ModelID,
                  YearID: model.YearID,
                  Region: model.Region,
                  EngineType: model.EngineType,
                  EngineCC: model.EngineCC,
                  VehicleType: model.VehicleType
                };
                return (
                  <tr key={idx}>
                    {Object.keys(row).map(col => {
                      const values = candidateModels.map(m => row[col]); // All values for this col
                      const isDiff = new Set(values).size > 1;
                      return (
                        <td key={col} style={{ border: '1px solid #ddd', padding: '0.5rem', fontWeight: isDiff ? 'bold' : 'normal' }}>
                          {row[col]}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            {candidateModels.map((model, idx) => (
              <button key={idx} onClick={() => {
                const handleModelSelect = onModelSelectRef.current;
                setShowModelSelector(false);
                setCandidateModels([]);
                onModelSelectRef.current = null;
                handleModelSelect?.(model.ModelKey);
              }}>
                Select #{idx + 1}
              </button>
            ))}
            <button onClick={() => {
              setShowModelSelector(false);
              setCandidateModels([]);
              onModelSelectRef.current = null;
            }}>Cancel</button>
          </div>
        </div>
      )}
      {/* ─── Controls ──────────────────────────── */}
      <div style={{ marginBottom: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'space-between' }}>
        {/* Group Selection Section (left, collapsible) */}
        <div style={{ border: '1px solid #ddd', padding: '0.5rem', borderRadius: '4px', flex: '1 1 300px' }}>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setIsGroupCollapsed(!isGroupCollapsed)} role="button" aria-expanded={!isGroupCollapsed}>
            <h3 style={{ margin: 0, flex: 1 }}>Group Selection</h3>
            {isGroupCollapsed ? <FaChevronDown /> : <FaChevronUp />}
          </div>
          {!isGroupCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
              <label>
                Product Group:{' '}
                <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}>
                  <option value="">— select —</option>
                  {groups.filter(g => g.ParentProductGroupKey === null).map(g => (
                    <option key={g.ProductGroupKey} value={g.ProductGroupKey}>{g.ProductGroup}</option>
                  ))}
                </select>
              </label>
              <label>
                Sub-Group:{' '}
                <select
                  value={selectedSubGroup}
                  onChange={e => setSelectedSubGroup(e.target.value)}
                  disabled={!subGroups.length}
                >
                  <option value="">— all —</option>
                  {subGroups.map(s => (
                    <option key={s.SubProductGroupKey} value={s.SubProductGroupKey}>{s.name}</option>
                  ))}
                </select>
              </label>
              <button onClick={() => { if (!loading) { setDataLoaded(true); setPage(1); loadOverview(false); } }} title="Load Overview (Alt+L)" accessKey="L" style={{ marginTop: '0.5rem' }}>
                Load Overview
              </button>
            </div>
          )}
        </div>

        {/* Layout Prefs Section (middle, collapsible) */}
        <div style={{ border: '1px solid #ddd', padding: '0.5rem', borderRadius: '4px', flex: '1 1 300px' }}>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setIsLayoutCollapsed(!isLayoutCollapsed)} role="button" aria-expanded={!isLayoutCollapsed}>
            <h3 style={{ margin: 0, flex: 1 }}>Layout Prefs</h3>
            {isLayoutCollapsed ? <FaChevronDown /> : <FaChevronUp />}
          </div>
          {!isLayoutCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button onClick={() => { setSaveStatus('saving'); savePrefs(); }} title="Save current column order and widths">
                Save Layout
              </button>
              <button onClick={reloadLayout} title="Reload saved column order and widths from storage">
                Reload Layout
              </button>
              <button onClick={() => {
                if (window.confirm('Reset to default layout? This change is temporary—reload to revert to your saved layout.')) {
                  const defaultOrder = [...DEFAULT_COLUMN_ORDER];
                  setColumnOrder(defaultOrder);
                  setColumnWidths({});
                  setPrevOrder([...defaultOrder]);
                  setPrevWidths({});
                }
              }} title="Reset columns to default order and widths (temporary – reload to revert)">
                Reset Layout
              </button>
              {saveStatus === 'saving' && <span style={{ color: '#ffa500' }}>Saving...</span>}
              {saveStatus === 'saved' && <span style={{ color: '#008000' }}>Saved!</span>}
              {saveStatus === 'error' && <span style={{ color: '#ff0000' }}>Save failed</span>}
            </div>
          )}
        </div>

        {/* Copy Tools Section (right, collapsible) */}
        <div style={{ border: '1px solid #ddd', padding: '0.5rem', borderRadius: '4px', flex: '1 1 300px' }}>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setIsCopyCollapsed(!isCopyCollapsed)} role="button" aria-expanded={!isCopyCollapsed}>
            <h3 style={{ margin: 0, flex: 1 }}>Copy Tools</h3>
            {isCopyCollapsed ? <FaChevronDown /> : <FaChevronUp />}
          </div>
          {!isCopyCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label title="Select source model to copy from">
  Copy From Model:
  <select value={copySourceModelKey} onChange={e => setCopySourceModelKey(Number(e.target.value))}>
    <option value="">— select —</option>
    {uniqueModels.filter(m => m.key !== selectedModelKey).map(m => (
      <option key={m.key} value={m.key}>{m.label}</option>
    ))}
  </select>
</label>
<label title="Current target model (based on selected cell)" style={{ marginLeft: '1rem' }}>
  Target Model: {uniqueModels.find(m => m.key === selectedModelKey)?.label || 'None selected'}
</label>
<label title="Toggle fields to copy (OEMNumber always copied; icons: checked = copy)">
  Fields:
  <div style={{ display: 'flex', gap: '0.5rem' }}>
    {Object.keys(copyFields).map(f => (
      <span key={f} title={`Toggle copy for ${f}`} style={{ cursor: 'pointer' }} onClick={() => setCopyFields(prev => ({ ...prev, [f]: !prev[f] }))}>
        {copyFields[f] ? <FaCheckCircle /> : <FaCircle />} {f}
      </span>
    ))}
  </div>
</label>
                <label title="Copy only to empty cells (avoids overwrites)">
                  <input type="checkbox" checked={copyOnlyEmpty} onChange={e => setCopyOnlyEmpty(e.target.checked)} disabled={permissions['Data Collection/Reviewing'] !== 'write'}/>
                  Only Empty Cells
                </label>
                {permissions['Data Collection/Reviewing'] === 'write' && (<button onClick={handleCopyFromModel} disabled={!copySourceModelKey} title="Copy selected fields from source to target model">
                  Copy
                </button>)}
                {copyProgress > 0 && copyProgress < 100 && <progress value={copyProgress} max="100" style={{ width: '100px' }} />}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {permissions['Data Collection/Reviewing'] === 'write' && (
                  <button onClick={() => handleAutoCopyFromYear('prev')} title="Auto-copy from previous year model (fetches if unloaded)">
                    Copy from Prev Year
                  </button>)}
                {permissions['Data Collection/Reviewing'] === 'write' && (
                  <button onClick={() => handleAutoCopyFromYear('next')} title="Auto-copy from next year model (fetches if unloaded)">
                    Copy from Next Year
                  </button>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Bar for Main Use Buttons (scrolls with page) */}
      <div ref={bottomBarRef} style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'var(--input-bg)',
        color: 'var(--text)',
        borderTop: '1px solid #ddd',
        padding: '0.5rem',
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem',
        boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
        zIndex: 1000
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>Core Actions:</span>

          <button
            onClick={() => {
              if (!loading) {
                setDataLoaded(true);
                setPage(1);
                loadOverview(false);
              }
            }}
            title="Load Overview (Alt+L)"
            accessKey="L"
          >
            Load Overview
          </button>

          <button
            onClick={() => {
              setFilters({
                Make: '',
                ModelName: '',
                ModelID: '',
                Year: '',
                YearID: '',
                Region: '',
                ItemType: '',
                FitmentLocation: '',
                SKUGroup: '',
                OEMNumber: '',
                Quantity: '',
                OEMNote: '',
                AdditionalFitmentInfo: ''
              });
              setPage(1);
              setDataLoaded(true);
              loadOverview(false);
            }}
            title="Clear Filters (Alt+C)"
            accessKey="C"
          >
            Clear Filters
          </button>

          <select
            value={fillValue}
            onChange={e => setFillValue(e.target.value)}
            style={{ marginLeft: '0.5rem' }}
          >
            <option value="" disabled>— select —</option>
            <option value="NOOEMFICHE">NOOEMFICHE</option>
            <option value="TBA">TBA</option>
            <option value="None">None</option>
            <option value="Custom">Custom</option>
          </select>

          {fillValue === 'Custom' && (
            <input
              type="text"
              value={customFillValue}
              onChange={e => setCustomFillValue(e.target.value)}
              placeholder="Custom value"
              style={{ marginLeft: '0.5rem', width: '150px' }}
              disabled={permissions['Data Collection/Reviewing'] !== 'write'}
            />
          )}

          {permissions['Data Collection/Reviewing'] === 'write' && (
            <button
              onClick={handleBulkFill}
              disabled={
                !selectedModelKey ||
                !fillValue ||
                (fillValue === 'Custom' && !customFillValue.trim())
              }
              title="Fill Empty OEMNumbers for selected model"
              style={{ marginLeft: '0.5rem' }}
            >
              Fill Empty OEMNumbers
            </button>
          )}

          {permissions['Data Collection/Reviewing'] === 'write' && (
            <button
              onClick={handleUndoLastFill}
              disabled={!lastBulkChanges.length || isUndone}
              title="Undo Last Fill"
            >
              Undo Last Fill
            </button>
          )}

          {permissions['Data Collection/Reviewing'] === 'write' && (
            <button
              onClick={handleRedoLastFill}
              disabled={!lastBulkChanges.length || !isUndone}
              title="Redo Last Fill"
            >
              Redo Last Fill
            </button>
          )}

          {/* NEW: Manage OEM button (opens modal; preselect current row's OEM when possible) */}
<button
  onClick={() => {
    const r = (focusPos?.row != null) ? rows[focusPos.row] : null;
    setManageInitialOEM((r?.OEMNumber || '').trim());
    setManageInitialOEMKey(r?.OEMKey ?? null);
    setManageOpen(true);
  }}
  title="Manage OEM (view/edit/create/delete)"
>
  Manage OEM
</button>

    <button
  onClick={() => {
    const r = (focusPos?.row != null) ? rows[focusPos.row] : null;
    const initialGroup = (r?.SKUGroup || '').trim();
    setManageInitialSKUGroup(initialGroup);
    setManageSkuOpen(true);
  }}
  title="Manage SKU Group (view/edit/create/delete)"
>
  Manage SKU
</button>

        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          borderLeft: '1px solid #ddd',
          paddingLeft: '1rem'
        }}>
          <span>Year Copy:</span>

          {permissions['Data Collection/Reviewing'] === 'write' && (
            <button
              onClick={() => handleAutoCopyFromYear('prev')}
              title="Copy from -1 Yr (previous year model, fetches if unloaded)"
            >
              -1 Yr
            </button>
          )}

          {permissions['Data Collection/Reviewing'] === 'write' && (
            <button
              onClick={() => handleAutoCopyFromYear('next')}
              title="Copy from +1 Yr (next year model, fetches if unloaded)"
            >
              +1 Yr
            </button>
          )}

          {/* One-Off picker trigger */}
          {permissions['Data Collection/Reviewing'] === 'write' && (
            <button
              onClick={() => {
                if (!selectedModelKey) {
                  alert('Select a model row first');
                  return;
                }
                openAddOneOff();
              }}
              title="Add a promoted One-Off location to the current model (ghost row until first edit)"
              style={{ marginLeft: '0.5rem' }}
            >
              Add One-Off Row
            </button>
          )}
          {/* Keep the modal mounted so Manage OEM works wherever this bar is */}
          <ManageOEMModal
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            initialOEMNumber={manageInitialOEM}
            initialOEMKey={manageInitialOEMKey}
            isDark={isDark} // Pass theme prop
            canEdit={canDataCollectionWrite}
          />

          <ManageSKUModal
            open={manageSkuOpen}
            onClose={() => setManageSkuOpen(false)}
            initialSKUGroup={manageInitialSKUGroup}
            isDark={isDark} // Pass theme prop
            canEdit={canDataCollectionWrite}
          />

          <KittingModal
            open={kitModalState.open}
            onClose={closeKitModal}
            initialKitId={kitModalState.kitId}
            fitmentContext={kitModalState.fitment}
            canEdit={canManageKits}
            onKitLinked={handleKitLinked}
            ensureFitment={ensureFitmentForKit}
          />

          <AddOneOffModal
            open={!!oneOffContext}
            onClose={() => setOneOffContext(null)}
            baseRow={oneOffContext?.baseRow || null}
            productGroupKey={oneOffContext?.productGroupKey || null}
            subProductGroupKey={oneOffContext?.subProductGroupKey || null}
            modelKey={oneOffContext?.modelKey || null}
            existingFitmentLocationKeys={oneOffContext?.existingKeys || []}
            onAddGhostRow={addGhostOneOffRow}
            onOneOffPromoted={handleOneOffPromoted}
            canCreateItemType={canDataCollectionWrite}
          />


        </div>
      </div>
      <div style={{ marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label>
            <input type="checkbox" checked={formatToggle} onChange={e => setFormatToggle(e.target.checked)} />
            Auto-format OEM Number on entry
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={showIncompleteOnly}
            onChange={e => {
              if (editing.rowIdx !== null) {
                setLastFocusedKey(rows[editing.rowIdx].FitmentKey);
                setLastFocusedField(editing.field);
                setLastFocusedCol(columnOrder.indexOf(editing.field));
              }
              setShowIncompleteOnly(e.target.checked);
              setPage(1);
              setRows([]);
              setHasMore(true);
              immediateLoadOverview(false);
              setDataLoaded(true);
            }}
          />
          <label>Show incomplete models only (blank OEMNumber)</label>
        </div>
      </div>
      {/* ─── Filters & Table ─────────────────────── */}
      <div ref={scrollRef} style={{
        height: gridHeight ? `${gridHeight}px` : 'calc(100vh - 350px)',
        overflowY: 'auto',
        border: '1px solid #ddd',
        scrollbarWidth: 'auto',
        scrollbarColor: '#888 #f1f1f1'
      }}>
        <table style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' }}>
          <colgroup>
            {columnOrder.map(key => (
              <col key={key} data-key={key} style={{ width: columnWidths[key] || 'auto' }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columnOrder.map((key, idx) => (
                <th 
                  key={key} 
                  style={{ position: 'sticky', top: 0, background: 'var(--input-bg)', color: 'var(--text)', zIndex: 1, overflow: 'hidden', userSelect: 'none' }}
                  // draggable removed to disable column reordering via drag
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    // Optional: Keep drop handler if needed, but since no drag, it won't trigger
                  }}
                >
                  {key}
                  <input
                    style={{ width: '90%', boxSizing: 'border-box', marginTop: '0.25em', userSelect: 'text' }}
                    value={filters[key]}
                    onChange={e => handleFilterChange(e, key)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Return') applyFilters(); }}  // Trigger immediate filter on Enter/Return
                    onMouseDown={e => e.stopPropagation()} // Prevent drag on input
                    draggable="false"
                    onDrop={e => e.preventDefault()} // Prevent drop inserting text
                    disabled={permissions['Data Collection/Reviewing'] !== 'write'}
                  />
                  <div
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px', cursor: 'col-resize', background: 'transparent' }}
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startWidth = scrollRef.current.querySelector(`col[data-key="${key}"]`)?.offsetWidth || 100;
                      const onMouseMove = (moveE) => {
                        const newWidth = Math.max(50, startWidth + moveE.clientX - startX) + 'px'; // Min 50px
                        setColumnWidths(prev => ({ ...prev, [key]: newWidth }));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
              ))}
              <th style={{ position: 'sticky', top: 0, background: 'var(--input-bg)', color: 'var(--text)', zIndex: 1, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows || rows.length === 0 ? (
              <tr>
                <td colSpan={columnOrder.length + 1} style={{ textAlign: 'center', padding: '1rem' }}> {/* +1 for Actions column */}
                  No rows match the current filters.
                </td>
              </tr>
            ) : (
                 (() => {
            const tableRows = [];
            let prevModelKey = null;
            const days30Ago = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
            rows.forEach((r, i) => {
              if (prevModelKey !== null && r.ModelKey !== prevModelKey) {
                tableRows.push(
                  <tr key={`divider-${i}`}>
                    <td colSpan={columnOrder.length + 1} style={{ background: 'orange', height: '5px', padding: 0 }}></td>
                  </tr>
                );
              }
              // Row-level calculations
              const RecentFields = r.RecentFields ? r.RecentFields.split(',').map(x => x.trim()) : [];
              const createdDate = r.DateCreated ? new Date(r.DateCreated) : null;
              const kitId = extractKitId(r);
              const directKitId = r.KitId ?? r.KitID ?? null;
              const inheritedKitId = !directKitId ? r.LinkedKitId ?? null : null;
              const normalizedOEMNumber = typeof r.OEMNumber === 'string' ? r.OEMNumber.trim() : '';
              const oemLooksLikeKit = normalizedOEMNumber.toUpperCase().startsWith(KIT_ID_PREFIX);
              const ownsKit = Boolean(
                directKitId || (!inheritedKitId && (oemLooksLikeKit || kitId))
              );
              const resolvedKitLabel = kitId || normalizedOEMNumber || inheritedKitId || '';
              const highlightColor = isDark ? '#00821E' : '#90EE90'; // Dark mode: #00821E, Light mode: #90EE90 //Highlight colour control DL 08.09.25
              const rowBgColor = (showHistoryHighlights && createdDate && createdDate > days30Ago) ? highlightColor : undefined;
              tableRows.push(
                <tr
                  key={i}
                  style={{
                    outline: focusPos.row === i ? '2px solid #66f' : undefined,
                    backgroundColor: rowBgColor
                  }}
                  ref={i === rows.length - 1 ? lastRowRef : null}
                >
                  {columnOrder.map((f, j) => {
                    const isEditableField = ['SKUGroup','OEMNumber','Quantity','OEMNote','AdditionalFitmentInfo', 'FicheURL'].includes(f); // Added FicheURL to editable
                    const isEditing = editing.rowIdx === i && editing.field === f;
                    const display = r[f];
                    const cellBgColor = (!rowBgColor && showHistoryHighlights && RecentFields.includes(f)) ? 'orange' : undefined;
                    const tabIdx = isEditableField ? (i * columnOrder.length + j) + 1 : -1; // Adjusted for more fields
                    const lockedUntilOem = (f === 'SKUGroup' || f === 'Quantity') && !String(r.OEMNumber ?? '').trim();
                  if (f === 'SKUGroup') {
                    return (
                      <td
                        key={f}
                        style={{
                          outline: focusPos.col === j ? '2px solid #66f' : undefined,
                          padding: '0.25em',
                          backgroundColor: cellBgColor,
                          cursor: 'default'
                        }}
                        onMouseDown={() => onFocusCell(i, j)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <input
                            ref={el => (inputRefs.current[`${i}-${j}`] = el)}
                            value={isEditing ? editing.value : display || ''}
                            onFocus={() => onFocusCell(i, j)}
                            onChange={e => handleChange(i, f, e.target.value)}
                            onBlur={() => handleBlur(i, f)}
                            onKeyDown={e => handleKeyDown(e, i, f, j)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '0.2em', ...(lockedUntilOem ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                            tabIndex={tabIdx}
                            readOnly={lockedUntilOem}
                            placeholder={lockedUntilOem ? 'OEM first' : undefined}
                            title={lockedUntilOem ? 'Enter an OEM number first' : undefined}
                            disabled={permissions['Data Collection/Reviewing'] !== 'write'}
                          />
                          {kitId && (
                            <button
                              type="button"
                              onClick={() => openKitModalForRow(r, i)}
                              style={{
                                fontSize: '0.7rem',
                                padding: '0.2rem 0.4rem',
                                borderRadius: '4px',
                                border: '1px solid #93c5fd',
                                background: '#e0f2fe',
                                color: '#1e3a8a'
                              }}
                              title="Open kit manager"
                              tabIndex={-1}
                            >
                              Kit
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  }
                  if (f === 'FicheURL') {
                    return (
                      <td
                        key={f}
          style={{
            outline: focusPos.col === j ? '2px solid #66f' : undefined,
            padding: '0.25em',
            backgroundColor: cellBgColor,
            cursor: 'default'
          }}
          onMouseDown={() => onFocusCell(i, j)}
        >
          <input
            ref={el => (inputRefs.current[`${i}-${j}`] = el)}
            value={isEditing ? editing.value : display || ''}
            onFocus={() => onFocusCell(i, j)}
            onChange={e => {
              handleChange(i, f, e.target.value);
              // Auto-fill logic for OEMNumber and Quantity (kept as-is, though not for FicheURL)
              if (f === 'OEMNumber' && e.target.value.length < 3 && currentFiche) {  // Short line
                handleChange.cancel(); // Cancel pending save of line number
                const extracted = JSON.parse(currentFiche.ExtractedData || '{}');
                const key = e.target.value;
                const entries = extracted[key];
                const line = parseInt(key, 10);
                if (entries) {
                  if (Array.isArray(entries) && entries.length > 1) {
                    // Show popup for multiples
                    setOemChoices(entries);
                    setOemPopupLine(line);
                    setOemPopupVisible(true);
                    setOemPopupRowIdx(i);
                  } else {
                    const entry = Array.isArray(entries) ? entries[0] : entries;
                    e.target.value = entry.oem || entry.superseded || '';  // Set single
                  }
                }
              }
              if (f === 'Quantity' && currentFiche) {
                const extracted = JSON.parse(currentFiche.ExtractedData || '{}');
                const suggestedQty = Object.values(extracted)[0]?.qty || 1;  // Example from first
                if (!e.target.value) e.target.value = suggestedQty.toString();
              }
            }}
            onBlur={async () => {
              handleBlur(i, f);
              // Trigger POST on blur and update local/cache on success
              if (editing.value) {
                let localFitKey = r.FitmentKey;
                if (!localFitKey) {
                  // Ghost row: create Fitment first
                  try {
                    const createRes = await axios.post('/api/fitments', {
                      modelKey: r.ModelKey,
                      fitmentLocationKey: r.FitmentLocationKey,
                      itemTypeKey: r.ItemTypeKey
                    });
                    localFitKey = createRes.data.updated.FitmentKey;
                    setRows(prev => prev.map((pr, pi) => pi === i ? { ...pr, FitmentKey: localFitKey } : pr));
                  } catch (createErr) {
                    console.error('Failed to create Fitment for ghost row:', createErr);
                    return;
                  }
                }
                try {
                  await axios.post(`/api/fitments/${localFitKey}/fiches`, { url: editing.value });
                  // Append the new URL locally (assumes comma-separated list)
                  setRows(prev => prev.map((pr, pi) => pi === i ? { 
                    ...pr, 
                    FicheURL: pr.FicheURL ? pr.FicheURL + ',' + editing.value : editing.value 
                  } : pr));
                  // Cache updates automatically via useEffect on rows change
                } catch (postErr) {
                  console.error('Failed to process fiche POST:', postErr);
                  // Continue without crashing PATCH
                }
              }
            }}
            onKeyDown={e => handleKeyDown(e, i, f, j)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.2em' }}
            tabIndex={tabIdx}
            disabled={permissions['Data Collection/Reviewing'] !== 'write'}
          />
          {display && (
            <button onClick={() => {
              axios.get(`/api/fitments/${r.FitmentKey}/fiches`).then(res => {
                if (res.data.length > 0) {
                  setCurrentFiche(res.data[0]);
                  setEditedExtracted(JSON.parse(res.data[0].ExtractedData || '{}'));
                  setFicheModalVisible(true);
                } else {
                  alert('No fiches found for this fitment.');
                }
              }).catch(err => console.error('Failed to load fiches:', err));
            }}>View Fiche</button>
          )}
        </td>
                    );
                  } else if (f === 'ModelName') {
  return (
    <td
      key={f}
      style={{
        outline: focusPos.col === j ? '2px solid #66f' : undefined,
        padding: '0.25em',
        backgroundColor: cellBgColor,
        cursor: 'default'
      }}
      onMouseDown={() => onFocusCell(i, j)}
    >
      <span>{display || ''}</span>
    </td>
  );
} else if (!isEditableField) {
                    return (
                      <td
                        key={f}
                        style={{
                          outline: focusPos.col === j ? '2px solid #66f' : undefined,
                          padding: '0.25em',
                          backgroundColor: cellBgColor,
                          cursor: 'default'
                        }}
                        onMouseDown={() => onFocusCell(i, j)}
                      >
                        {display || ''}
                      </td>
                    );
                  }
                  // Existing editable input (with auto-fill added)
                  return (
                    <td
                      key={f}
                      style={{
                        outline: focusPos.col === j ? '2px solid #66f' : undefined,
                        padding: '0.25em',
                        backgroundColor: cellBgColor,
                        cursor: 'default'
                      }}
                      onMouseDown={() => onFocusCell(i, j)}
                    >
                      <input
                        ref={el => (inputRefs.current[`${i}-${j}`] = el)}
                        value={isEditing ? editing.value : display || ''}
                        onFocus={() => onFocusCell(i, j)}
onChange={e => {
  handleChange(i, f, e.target.value);
  // Auto-fill logic for OEMNumber and Quantity
          if (f === 'OEMNumber' && e.target.value.length < 3 && currentFiche) {  // Short line
            handleChange.cancel(); // Cancel pending save of line number
            const extracted = JSON.parse(currentFiche.ExtractedData || '{}');
            const key = e.target.value;
            const entries = extracted[key];
            const line = parseInt(key, 10);
            if (entries) {
              if (Array.isArray(entries) && entries.length > 1) {
                // Show popup for multiples
                setOemChoices(entries);
                setOemPopupLine(line);
                setOemPopupVisible(true);
                setOemPopupRowIdx(i);
              } else {
                const entry = Array.isArray(entries) ? entries[0] : entries;
                e.target.value = entry.oem || entry.superseded || '';  // Set single
              }
            }
          }
  if (f === 'Quantity' && currentFiche) {
    const extracted = JSON.parse(currentFiche.ExtractedData || '{}');
    const suggestedQty = Object.values(extracted)[0]?.qty || 1;  // Example from first
    if (!e.target.value) e.target.value = suggestedQty.toString();
  }
}}
                        onBlur={() => {
                          if (f === 'OEMNumber') {
                            handleOEMBlur(i);
                          } else {
                            handleBlur(i, f);
                          }
                        }}
                        onKeyDown={e => handleKeyDown(e, i, f, j)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '0.2em', ...(lockedUntilOem ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                        tabIndex={tabIdx}
                        readOnly={lockedUntilOem}
                        placeholder={lockedUntilOem ? 'OEM first' : undefined}
                        title={lockedUntilOem ? 'Enter an OEM number first' : undefined}
                        disabled={permissions['Data Collection/Reviewing'] !== 'write'}
                      />
                    </td>
                  );
                })}
                <td style={{ whiteSpace: 'nowrap', textAlign: 'left', padding: '4px' }}>
                  {canDataCollectionWrite && (
                    <button
                      onClick={() => openModelOverview(r)}
                      className="btn btn-sm"
                      style={{
                        display: 'inline-block',
                        marginRight: 6,
                        height: 28,
                        lineHeight: '26px',
                        padding: '0 10px',
                        borderRadius: 6
                      }}
                    >
                      Model View
                    </button>
                  )}
                  {canManageKits && (
                    <button
                      onClick={() => openKitModalForRow(r, i)}
                      className="btn btn-sm"
                      style={{
                        display: 'inline-block',
                        marginRight: 6,
                        height: 28,
                        lineHeight: '26px',
                        padding: '0 10px',
                        borderRadius: 6,
                        background: ownsKit
                          ? '#2563eb'
                          : (r.FitmentKey ? '#e0f2fe' : '#f3f4f6'),
                        color: ownsKit
                          ? '#fff'
                          : (r.FitmentKey ? '#1e3a8a' : '#374151'),
                        border: ownsKit
                          ? '1px solid #1d4ed8'
                          : (r.FitmentKey ? '1px solid #93c5fd' : '1px solid #d1d5db')
                      }}
                      title={ownsKit
                        ? resolvedKitLabel
                          ? `Manage Kit ${resolvedKitLabel}`
                          : 'Manage this kit'
                        : inheritedKitId
                        ? `This row is using kit ${inheritedKitId || kitId}. Choose New Kit to create a fitment-specific kit.`
                        : r.FitmentKey
                        ? 'Create or edit a kit for this fitment.'
                        : 'Create a kit and add this fitment line'}
                    >
                      {ownsKit ? 'Manage Kit' : 'New Kit'}
                    </button>
                  )}
                  {r.FitmentKey && (
                    <button
                      onClick={() => openHistory(r.FitmentKey)}
                      className="btn btn-sm"
                      style={{
                        display: 'inline-block',
                        marginRight: 6,
                        height: 28,
                        lineHeight: '26px',
                        padding: '0 10px',
                        borderRadius: 6
                      }}
                    >
                      History
                    </button>
                  )}
                  {isOneOffRow(r) && (
                    <button
                      onClick={() => handleRemoveOneOffRow(r)}
                      title="Remove one-off row"
                      className="btn btn-sm"
                      style={{
                        display: 'inline-block',
                        background: '#dc2626',
                        color: '#fff',
                        border: '1px solid #b91c1c',
                        height: 28,
                        lineHeight: '26px',
                        padding: '0 10px',
                        borderRadius: 6
                      }}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            );
            prevModelKey = r.ModelKey;
          });
          return tableRows;
        })()
      )}
    </tbody>
          </table>
          {historyVisible && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.45)',
                zIndex: 1500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.5rem'
              }}
              onMouseDown={closeHistory}
            >
              <div
                style={{
                  width: 'min(80vw, 1400px)',
                  maxHeight: '85vh',
                  background: 'var(--input-bg)',
                  color: 'var(--text)',
                  borderRadius: 12,
                  boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
                  padding: '1.25rem',
                  overflowY: 'auto',
                  position: 'relative'
                }}
                onMouseDown={e => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0 }}>History for Fitment {historyFitmentKey}</h3>
                  <button
                    onClick={closeHistory}
                    style={{
                      border: 'none',
                      borderRadius: 6,
                      padding: '0.35rem 0.75rem',
                      cursor: 'pointer',
                      background: 'var(--button-bg, #e5e7eb)',
                      color: 'var(--button-fg, #111)'
                    }}
                  >
                    Close
                  </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>User</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>ModelKey</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Make</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Model</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Year</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>ItemType</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>FitmentLocation</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Field</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Old</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>New</th>
                      <th style={{ textAlign: 'center', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Undo</th>
                      <th style={{ textAlign: 'center', padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Redo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRecords.map((h, i) => (
                      <tr key={i}>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{new Date(h.ChangeTime).toLocaleString()}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.ChangedBy}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                          {Number.isInteger(h.ModelKey)
                            ? (
                              <button
                                type="button"
                                onClick={() => setModelOverviewModal({
                                  modelKey: h.ModelKey,
                                  label: formatModelLabel(
                                    { make: h.Make, modelName: h.ModelName, year: h.Year },
                                    `Model ${h.ModelKey}`
                                  )
                                })}
                                style={{
                                  border: 'none',
                                  background: 'none',
                                  color: '#2563eb',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  padding: 0
                                }}
                                title={`Open model ${h.ModelKey}`}
                              >
                                {h.ModelKey}
                              </button>
                            )
                            : '—'}
                        </td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.Make}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.ModelName}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.Year}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.ItemType}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.FitmentLocation}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.FieldName}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.OldValue}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{h.NewValue}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)', textAlign: 'center' }}>
                          <button
                            onClick={() => handleRevert(h)}
                            disabled={!canDataCollectionWrite}
                            title={canDataCollectionWrite ? 'Undo change' : 'Requires write permission'}
                          >
                            Undo
                          </button>
                        </td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid rgba(0,0,0,0.05)', textAlign: 'center' }}>
                          <button
                            onClick={() => handleRedo(h)}
                            disabled={!canDataCollectionWrite}
                            title={canDataCollectionWrite ? 'Redo change' : 'Requires write permission'}
                          >
                            Redo
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
  {oemPopupVisible && (
    <div style={{ position: 'fixed', top: '10%', left: '10%', width: '80%', height: '80%', background: 'var(--input-bg)', color: 'var(--text)', zIndex: 1000, display: 'flex', overflow: 'hidden' }}>
      {/* Close button */}
      <button onClick={() => setOemPopupVisible(false)} style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>Close</button>
      
      {/* Left: Fiche image with zoom/pan */}
      <div style={{ flex: '0 0 70%', position: 'relative', overflow: 'auto' }}>
        <TransformWrapper initialScale={1} initialPositionX={0} initialPositionY={0}>
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent>
                <img src={oemChoices[selectedIndex]?.screenshotPath} alt="Fiche" style={{ width: '100%' }} />
              </TransformComponent>
              {/* Zoom controls */}
              <div style={{ position: 'absolute', bottom: 10, left: 10, display: 'flex', gap: 5, zIndex: 10 }}>
                <button onClick={() => zoomIn()}>+</button>
                <button onClick={() => zoomOut()}>-</button>
                <button onClick={() => resetTransform()}>Reset</button>
              </div>
            </>
          )}
        </TransformWrapper>
      </div>
      
      {/* Right: Variant choices */}
      <div style={{ flex: '0 0 30%', padding: '1rem', overflowY: 'auto', borderLeft: '1px solid #ddd' }}>
        <h3>Select Variant for Line {oemPopupLine} ({oemChoices.length} options)</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {oemChoices.map((opt, idx) => (
            <li
              key={idx}
              tabIndex={0}
              onClick={() => setSelectedIndex(idx)}
              onDoubleClick={async () => {
                try {
                  const part = oemChoices[idx];
                  if (!part) return;
                  const rowIdx = oemPopupRowIdx;
                  const fitKey = rows[rowIdx]?.FitmentKey;
                  if (!fitKey) return;
                  setRows((prev) =>
                    prev.map((r, i) =>
                      i === rowIdx ? { ...r, OEMNumber: part.oem || '', Quantity: part.qty || '1' } : r
                    )
                  );
                  await axios.patch(`/api/fitments/${fitKey}`, {
                    field: 'OEMNumber',
                    value: part.oem || ''
                  });
                  await axios.patch(`/api/fitments/${fitKey}`, {
                    field: 'Quantity',
                    value: part.qty || '1'
                  });
                  // Refetch full row to update SKUGroup/OEMNote
                  const refetchRes = await axios.get(`/api/fitments/${fitKey}`);
                  const updatedRow = refetchRes.data;
                  setRows((prev) =>
                    prev.map((r, idx) =>
                      idx === rowIdx ? { ...r, ...updatedRow } : r
                    )
                  );
                  setOemPopupVisible(false);
                } catch (dblErr) {
                  console.error('Popup double-click save failed:', dblErr);
                }
              }}
              style={{ cursor: 'pointer', padding: '0.5rem', background: selectedIndex === idx ? '#d0e4f7' : 'transparent', borderBottom: '1px solid #eee' }}
            >
              {opt.desc} (OEM: {opt.oem}, Manufacturer: {opt.manufacturer || 'Honda'})
            </li>
          ))}
        </ul>
        <button
          onClick={async () => {
            try {
              const part = oemChoices[selectedIndex];
              if (!part) return;
              const rowIdx = oemPopupRowIdx;
              const fitKey = rows[rowIdx]?.FitmentKey;
              if (!fitKey) return;
              setRows((prev) =>
                prev.map((r, i) =>
                  i === rowIdx ? { ...r, OEMNumber: part.oem || '', Quantity: part.qty || '1' } : r
                )
              );
              await axios.patch(`/api/fitments/${fitKey}`, {
                field: 'OEMNumber',
                value: part.oem || ''
              });
              await axios.patch(`/api/fitments/${fitKey}`, {
                field: 'Quantity',
                value: part.qty || '1'
              });
              // Refetch full row to update SKUGroup/OEMNote
              const refetchRes = await axios.get(`/api/fitments/${fitKey}`);
              const updatedRow = refetchRes.data;
              setRows((prev) =>
                prev.map((r, idx) =>
                  idx === rowIdx ? { ...r, ...updatedRow } : r
                )
              );
              setOemPopupVisible(false);
            } catch (saveErr) {
              console.error('Popup save failed:', saveErr);
            }
          }}
          style={{ marginRight: '1rem' }}
        >
          Save Selection
        </button>
        <button onClick={() => setOemPopupVisible(false)}>Cancel</button>
      </div>
    </div>
  )}

  {ficheModalVisible && (
    <div style={{ position: 'fixed', top: '10%', left: '10%', width: '80%', height: '80%', background: 'var(--input-bg)', color: 'var(--text)', zIndex: 1000, display: 'flex', overflow: 'hidden' }}>
      {/* Close button fixed */}
      <button onClick={() => setFicheModalVisible(false)} style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>Close</button>
      
      {/* Left: Image with zoom/pan */}
      <div style={{ flex: '0 0 70%', position: 'relative', overflow: 'auto' }}>
        <TransformWrapper initialScale={1} initialPositionX={0} initialPositionY={0}>
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent>
                <img src={currentFiche.ScreenshotPath} alt="Fiche" style={{ width: '100%' }} />
              </TransformComponent>
              {/* Zoom controls fixed bottom-left */}
              <div style={{ position: 'absolute', bottom: 10, left: 10, display: 'flex', gap: 5, zIndex: 10 }}>
                <button onClick={() => zoomIn()}>+</button>
                <button onClick={() => zoomOut()}>-</button>
                <button onClick={() => resetTransform()}>Reset</button>
              </div>
            </>
          )}
        </TransformWrapper>
      </div>
      
      {/* Right: JSON editor sidebar */}
      <div style={{ flex: '0 0 30%', padding: '1rem', overflowY: 'auto', borderLeft: '1px solid #ddd' }}>
        <h3>Full Page Source</h3>
        <button 
          onClick={() => {
            const htmlUrl = currentFiche.ScreenshotPath.replace('.jpg', '.txt');
            window.open(htmlUrl, '_blank');
          }}
          style={{ marginBottom: '1rem', padding: '6px 12px', borderRadius: 4, background: '#007bff', color: 'white', border: 'none', cursor: 'pointer' }}
        >
          View Raw Source Code
        </button>
        <h3>Extracted Data (Editable)</h3>
        <textarea 
          value={JSON.stringify(editedExtracted, null, 2)} 
          onChange={e => setEditedExtracted(JSON.parse(e.target.value))} 
          style={{ width: '100%', height: '200px' }}
        />
<button onClick={async () => {
  await axios.patch(`/api/fiches/${currentFiche.FicheImageKey}`, { extracted: editedExtracted });
}}>Save Edits</button>
<button onClick={() => {
const multiLines = Object.entries(editedExtracted).filter(([,v]) => Array.isArray(v) && v.length > 1);
if (!multiLines.length) return alert('No multiples to resolve');
// Sequential prompts
multiLines.reduce((p, [k, arr]) => p.then(() => new Promise(res => {
  const choice = prompt(`Choose for line ${k} (enter index 1-${arr.length}): \n` + arr.map((e,i) => `${i+1}: ${e.oem || e.superseded} (${e.desc})`).join('\n'));
  const idx = parseInt(choice, 10) - 1;
  if (idx >= 0 && idx < arr.length) {
    setEditedExtracted(prev => ({ ...prev, [k]: { ...arr[idx], selected: true } })); // Flatten to selected
  }
  res();
})), Promise.resolve()).then(() => alert('Resolves saved - edit JSON if needed'));
}}>Resolve Multiples</button>
<button onClick={async () => {
  const updatedExtracted = { ...editedExtracted };
  for (const [line, data] of Object.entries(updatedExtracted)) {
    const processPart = async (part) => {
      if (part.oem && part.manufacturer) {
        const { data: { formatted } } = await axios.post('/api/format-oem', {
          oem: part.oem,
          manufacturer: part.manufacturer
        });
        part.oem = formatted;
      }
    };
    if (Array.isArray(data)) {
      for (const part of data) {
        await processPart(part);
      }
    } else if (typeof data === 'object' && data !== null) {
      await processPart(data);
    }
  }
  setEditedExtracted(updatedExtracted);
}}>Re-Format OEMs</button>
      </div>
    </div>
  )}

  {/* ---------- 1. Model-overview modal (opened from the “Model View” button) ---------- */}
{modelOverviewModal && (
  <DataCollectionOverviewByModel
    modelKey={modelOverviewModal.modelKey}
    onClose={closeModelOverview}
    permissions={permissions}
    isDark={isDark}
    label={modelOverviewModal.label}
    formatToggle={formatToggle}
    onFormatToggleChange={setFormatToggle}
    resolveOEMTypeFast={resolveOEMTypeFast}
    onRequestYearChange={(targetMeta) => {
      const modelKey = targetMeta?.modelKey ?? targetMeta?.ModelKey;
      if (!modelKey) {
        window.alert('No matching year found for this make/model/region.');
        return;
      }

      setModelOverviewModal((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          modelKey,
          label: formatModelLabel(targetMeta, prev.label)
        };
      });
    }}
  />
)}


  {/* ---------- Loading indicator ---------- */}
  {loading && (
    <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
      Loading more rows...
    </div>
  )}
</div>

    </div>
  );
}
